import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Card,
  Form,
  Input,
  DatePicker,
  Select,
  InputNumber,
  Button,
  Tabs,
  Table,
  Space,
  Modal,
  Drawer,
  Typography,
  Row,
  Col,
  Divider,
  Tag,
  Popconfirm,
  Statistic,
  Switch,
  Badge,
  Descriptions,
  Alert,
  message,
  Spin,
  Radio,
} from 'antd';
import {
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
  CalculatorOutlined,
  ArrowLeftOutlined,
  EditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ToolOutlined,
  SearchOutlined,
  PaperClipOutlined,
  FileTextOutlined,
  SwapOutlined,
  SyncOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import { openBoqEstimationPrint } from '@/utils/openBoqEstimationPrint';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import { SalesTeamTransferModal } from '@/components/sales/SalesTeamTransferModal';
import { TransferHistoryPanel } from '@/components/sales/TransferHistoryPanel';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api.service';
import {
  BOQ_KEYS,
  useBOQ,
  useCreateBOQ,
  useUpdateBOQ,
  useAddLineItems,
  useUpdateLineItem,
  useDeleteLineItem,
  useRecalculateBOQ,
  useSubmitBOQForReview,
  useApproveBOQ,
  useRejectBOQ,
  useBOQMaterials,
  useAddBOQMaterial,
  useUpdateBOQMaterial,
  useDeleteBOQMaterial,
  useBOQOperations,
  useAddBOQOperation,
  useUpdateBOQOperation,
  useDeleteBOQOperation,
  useToggleBOQManualCost,
  useUpdateBOQLineItemMargin,
  useRecalculateBOQLineItem,
  useBoqLineItemPricingHistory,
  useCopyBoqLineItemPricing
} from '../../hooks/useBOQ';
import type {
  BOQRequest,
  BOQHeader,
  LineItemRequest,
  BOQLineItem,
  BOQCategory,
  BOQLineItemMaterial,
  BOQLineItemOperation,
  MaterialRequest,
  OperationRequest,
  BoqLineItemPricingHistoryRow
} from '../../services/boq.service';
import { boqService } from '../../services/boq.service';
import { documentKeys } from '../../hooks/useDocuments';
import { useActiveCustomers } from '../../hooks/useCustomers';
import { useOpportunities, useOpportunity } from '../../hooks/useOpportunities';
import { useCurrency } from '../../contexts/CurrencyContext';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import operationService, { Operation } from '../../services/operation.service';
import inventoryService, { Item } from '../../services/inventory.service';
import { ItemSelect } from '../../components/inventory/ItemSelect';
import { QuickCreateOperationModal } from '../../components/manufacturing/QuickCreateOperationModal';
import { ProjectSelector } from '../../components/project';
import DocumentList from '../../components/documents/DocumentList';
import InheritedAttachmentsCard from '../../components/documents/InheritedAttachmentsCard';
import DocumentUpload from '../../components/documents/DocumentUpload';
import { EntityAttachmentsCard } from '../../components/documents/EntityAttachmentsCard';
import {
  BOQ_LINE_DOCUMENT_CATEGORY_ATTACHMENT,
  BOQ_LINE_DOCUMENT_CATEGORY_TECHNICAL,
  DOCUMENT_ENTITY_TYPE_BOQ_LINE_ITEM,
} from '@/constants/document-entity-types';
import { useDocumentBreadcrumb } from '@/contexts/PageBreadcrumbContext';
import { useCreateQuotationFromBOQ, useCreateQuotationRevision } from '../../hooks/useQuotation';
import type { Quotation } from '../../services/quotation.service';
import { displayQuotationRevisionLabel } from '../../utils/quotationRevisionPolicy';
import { useAuthStore } from '@/store/authStore';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { projectService } from '@/services/project.service';
import {
  computeBoqSellingPrices,
  marginFromSelling,
  roundBoqMarginPct,
  roundBoqMoney,
  sellingFromMargin,
} from '../../utils/boqLineItemPricing.util';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea, Search: InputSearch } = Input;

function unwrapProcurementList<T>(res: unknown): T {
  const data = (res as { data?: unknown })?.data ?? res;
  return (data as { data?: T })?.data ?? (data as T);
}

interface SupplierCategoryListRow {
  id: string;
  category_name: string;
}

interface CategoryData {
  id?: string;
  name: string;
  items: (LineItemRequest & { lineItemId?: string; boqLineItem?: BOQLineItem })[];
}

type LineItemRow = LineItemRequest & { lineItemId?: string; boqLineItem?: BOQLineItem };

function lineItemBreakdownTotal(li: BOQLineItem): number {
  return (
    Number(li.material_cost || 0) +
    Number(li.labor_cost || 0) +
    Number(li.overhead_cost || 0) +
    Number(li.machine_cost || 0) +
    Number(li.tooling_cost || 0) +
    Number(li.operation_outsourced_cost || 0)
  );
}

/** Unit cost from materials/operations when a breakdown exists; otherwise basic-info unit_cost. */
function effectiveBoqLineUnitCost(li: BOQLineItem): number {
  const breakdownTotal = lineItemBreakdownTotal(li);
  if (breakdownTotal > 0) {
    return breakdownTotal;
  }
  return Number(li.unit_cost) || Number(li.calculated_unit_cost ?? 0);
}

function getEffectiveMarginForLineItem(item: LineItemRow, boqMargin: number): number {
  const li = item.boqLineItem;
  if (li && li.margin_percentage !== null && li.margin_percentage !== undefined) {
    return Number(li.margin_percentage);
  }
  if (item.marginPercentage !== null && item.marginPercentage !== undefined) {
    return Number(item.marginPercentage);
  }
  return boqMargin;
}

function getUnitCostBasis(item: LineItemRow): number {
  if (item.boqLineItem) {
    return effectiveBoqLineUnitCost(item.boqLineItem);
  }
  return Number(item.unitCost || 0);
}

function getLineCostTotalForLineItem(item: LineItemRow): number {
  const li = item.boqLineItem;
  const qty = Number(li?.quantity) || Number(item.quantity) || 0;
  if (li) {
    const unit = effectiveBoqLineUnitCost(li);
    const fromUnit = qty * unit;
    if (unit > 0) return fromUnit;
    const stored = Number(li.line_total ?? 0);
    return stored > 0 ? stored : fromUnit;
  }
  return qty * getUnitCostBasis(item);
}

function getUnitSellingPriceForLineItem(item: LineItemRow, boqMargin: number): number {
  const unitCost = getUnitCostBasis(item);
  const qty = Number(item.quantity) || 0;
  const m = getEffectiveMarginForLineItem(item, boqMargin);
  const computed = computeBoqSellingPrices(unitCost, qty, m).unitSellingPrice;
  const li = item.boqLineItem;
  if (li) {
    const stored = Number(li.unit_selling_price);
    if (Number.isFinite(stored) && stored > 0 && Math.abs(stored - computed) < 0.05) {
      return stored;
    }
  }
  return computed;
}

function getLineSellingTotalForLineItem(item: LineItemRow, boqMargin: number): number {
  const unitCost = getUnitCostBasis(item);
  const qty = Number(item.quantity) || 0;
  const m = getEffectiveMarginForLineItem(item, boqMargin);
  const computed = computeBoqSellingPrices(unitCost, qty, m).lineSellingTotal;
  const li = item.boqLineItem;
  if (li) {
    const stored = Number(li.line_selling_total);
    if (Number.isFinite(stored) && stored > 0 && Math.abs(stored - computed) < 0.05) {
      return stored;
    }
  }
  return computed;
}

const BOQBuilderPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [itemForm] = Form.useForm();
  const watchedBoqMargin = Form.useWatch('marginPercentage', form);

  // State
  const [categories, setCategories] = useState<CategoryData[]>([
    { name: 'General Items', items: [] }
  ]);
  const [activeCategory, setActiveCategory] = useState('0');
  const [itemModalVisible, setItemModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<{ categoryIndex: number; itemIndex: number; lineItemId?: string; boqLineItem?: BOQLineItem } | null>(null);
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  
  // New state for materials & operations
  const [activeTab, setActiveTab] = useState('basic');
  const [materialModalVisible, setMaterialModalVisible] = useState(false);
  const [operationModalVisible, setOperationModalVisible] = useState(false);
  const [quickCreateOperationOpen, setQuickCreateOperationOpen] = useState(false);
  const [operationSelectSearch, setOperationSelectSearch] = useState('');
  const [operationModalSaving, setOperationModalSaving] = useState(false);
  const operationModalSaveLockRef = useRef(false);
  const [materialModalSaving, setMaterialModalSaving] = useState(false);
  const materialModalSaveLockRef = useRef(false);
  const [editingMaterial, setEditingMaterial] = useState<BOQLineItemMaterial | null>(null);
  const [editingOperation, setEditingOperation] = useState<BOQLineItemOperation | null>(null);
  const [materialForm] = Form.useForm();
  const [operationForm] = Form.useForm();
  const [currentLineItemId, setCurrentLineItemId] = useState<string | null>(null);
  const [operations, setOperations] = useState<Operation[]>([]);
  const [isLoadingOperations, setIsLoadingOperations] = useState(false);
  const [items, setItems] = useState<Item[]>([]);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [projectHasPartnerSO, setProjectHasPartnerSO] = useState(false);
  const [partnerSONumber, setPartnerSONumber] = useState<string>('');
  const [pricingHistoryModalOpen, setPricingHistoryModalOpen] = useState(false);
  const [pricingHistoryQuery, setPricingHistoryQuery] = useState('');
  const [historySearchText, setHistorySearchText] = useState('');
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [selectedConvertLineIds, setSelectedConvertLineIds] = useState<string[]>([]);
  const [convertMode, setConvertMode] = useState<'new' | 'revise'>('new');
  const [reviseTargetQuotationId, setReviseTargetQuotationId] = useState<string | undefined>();
  const [convertDepartmentId, setConvertDepartmentId] = useState<string | undefined>();
  const [transferOpen, setTransferOpen] = useState(false);
  const [syncingOpportunityDocs, setSyncingOpportunityDocs] = useState(false);

  // Queries and Mutations
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { data: boqData, isLoading: isLoadingBOQ } = useBOQ(id!, { enabled: !!id });
  const createQuotationFromBoqMutation = useCreateQuotationFromBOQ();
  const createQuotationRevisionMutation = useCreateQuotationRevision();
  const { data: relatedQuotations = [] } = useQuery({
    queryKey: ['quotations', 'by-boq', id],
    queryFn: async () => {
      if (!id) return [] as Quotation[];
      const res = await api.get(`/crm/quotations?boq_id=${encodeURIComponent(id)}`);
      const body = (res as { data?: unknown })?.data ?? res;
      const rows = Array.isArray((body as { data?: unknown })?.data)
        ? ((body as { data: Quotation[] }).data || [])
        : Array.isArray(body)
          ? (body as Quotation[])
          : [];
      return Array.isArray(rows) ? rows : [];
    },
    enabled: !!id,
  });

  /** After server updates a line (cost mode, margin, recalc), refetch BOQ and sync drawer + Basic Info form. */
  const patchLineItemDrawerAfterBoqRefetch = React.useCallback(
    async (lineItemId: string) => {
      if (!id) return;
      await queryClient.refetchQueries({ queryKey: BOQ_KEYS.detail(id) });
      const fresh = queryClient.getQueryData(BOQ_KEYS.detail(id)) as BOQHeader | undefined;
      const li = fresh?.lineItems?.find((l) => l.id === lineItemId);
      if (!li) return;
      setEditingItem((prev) =>
        prev?.lineItemId === lineItemId ? { ...prev, boqLineItem: li } : prev
      );
      itemForm.setFieldsValue({
        itemCode: li.item_code,
        itemName: li.item_name,
        description: li.description,
        revision: li.revision,
        quantity: li.quantity,
        uom: li.uom,
        unitCost: effectiveBoqLineUnitCost(li),
        costBasis: li.cost_basis,
        supplierName: li.supplier_name,
        leadTimeDays: li.lead_time_days,
        notes: li.notes,
        marginPercentage:
          li.margin_percentage !== null && li.margin_percentage !== undefined
            ? Number(li.margin_percentage)
            : undefined
      });
    },
    [id, queryClient, itemForm]
  );
  const { data: customers, isLoading: isLoadingCustomers } = useActiveCustomers();
  const opportunityIdWatch = Form.useWatch('opportunityId', form);
  const { data: openOpportunities = [] } = useOpportunities({ status: 'Open' });
  const linkedOppIdForFetch =
    opportunityIdWatch && String(opportunityIdWatch).trim() ? String(opportunityIdWatch).trim() : undefined;
  const { data: linkedOppDetail } = useOpportunity(linkedOppIdForFetch);
  const opportunitySelectOptions = useMemo(() => {
    const list = [...openOpportunities];
    if (linkedOppDetail && !list.some((o) => o.id === linkedOppDetail.id)) {
      list.push(linkedOppDetail);
    }
    return list;
  }, [openOpportunities, linkedOppDetail]);
  const { data: revenueServiceTypes = [] } = useQuery({
    queryKey: ['sales-revenue-service-types', 'active'],
    queryFn: async () => {
      const res = await api.get('/sales/revenue-service-types?only_active=true');
      const body = (res as { data?: unknown })?.data ?? res;
      const list = Array.isArray(body) ? body : Array.isArray((body as { data?: unknown })?.data) ? (body as { data: unknown[] }).data : [];
      return list as { id: string; service_code: string; service_name: string }[];
    },
  });
  const rstSelectOptions = useMemo(
    () => revenueServiceTypes.map((r) => ({ value: r.id, label: `${r.service_code} — ${r.service_name}` })),
    [revenueServiceTypes]
  );
  const isSuperAdmin = Boolean(user?.is_super_admin);
  const userPerms = user?.permissions || [];
  const canConvertBoqToQuotation =
    isSuperAdmin || userPerms.includes('crm.quotation.create') || userPerms.includes('growth.view');
  const boqStatusAllowsConversion = ['Approved', 'Converted'].includes(String(boqData?.status || ''));
  const convertLineCandidates = useMemo(() => boqData?.lineItems || [], [boqData?.lineItems]);
  const lineIdsAlreadyQuoted = useMemo(() => {
    const ids = new Set<string>();
    for (const q of relatedQuotations || []) {
      for (const item of q.items || []) {
        const lineId = String(item.boq_line_item_id || '').trim();
        if (lineId) ids.add(lineId);
      }
    }
    return ids;
  }, [relatedQuotations]);
  const handleOpenConvertModal = async () => {
    if (!id) return;
    const allIds = (boqData?.lineItems || []).map((line) => line.id);
    setSelectedConvertLineIds(allIds);
    const tip = [...(relatedQuotations || [])].sort(
      (a, b) => Number(b.revision_number || 0) - Number(a.revision_number || 0)
    )[0];
    if (tip?.id) {
      setConvertMode('revise');
      setReviseTargetQuotationId(tip.id);
    } else {
      setConvertMode('new');
      setReviseTargetQuotationId(undefined);
    }
    setConvertDepartmentId(undefined);
    setConvertModalOpen(true);
    const projectId = boqData?.project_id ? String(boqData.project_id).trim() : '';
    if (projectId) {
      try {
        const project = await projectService.getProjectById(projectId);
        const deptId = project?.department_id ? String(project.department_id).trim() : '';
        if (deptId) setConvertDepartmentId(deptId);
      } catch {
        // Prefill is best-effort; user can still pick department in the modal.
      }
    }
  };
  const handleConvertSelectedLines = async () => {
    if (!id) return;
    if (convertMode === 'revise') {
      if (!reviseTargetQuotationId) {
        message.warning('Select an existing quotation to revise.');
        return;
      }
      try {
        const res = await createQuotationRevisionMutation.mutateAsync({
          id: reviseTargetQuotationId,
          revisionReason: `Estimate/BOQ update from ${boqData?.boq_number || id}`,
          revisedData: {
            revision_type: 'ESTIMATE_CHANGE',
            estimate_source: 'keep',
          },
        });
        const quotationId = ((res as { data?: { id?: string }; id?: string })?.data?.id ||
          (res as { id?: string })?.id) as string | undefined;
        message.success('Quotation revision created. Review commercial lines on the new draft.');
        setConvertModalOpen(false);
        if (quotationId) navigate(`/growth/quotations/${quotationId}`);
        await queryClient.invalidateQueries({ queryKey: ['quotations'] });
        await queryClient.refetchQueries({ queryKey: ['quotations', 'by-boq', id] });
      } catch (error: any) {
        message.error(error?.response?.data?.message || error?.message || 'Failed to create revision');
      }
      return;
    }
    if (selectedConvertLineIds.length === 0) {
      message.warning('Select at least one BOQ line item.');
      return;
    }
    if (!convertDepartmentId?.trim()) {
      message.warning('Department is required');
      return;
    }
    try {
      const revenueServiceTypeId =
        form.getFieldValue('revenueServiceTypeId') ||
        boqData?.revenue_service_type_id ||
        undefined;
      const res = await createQuotationFromBoqMutation.mutateAsync({
        boqId: id,
        opportunityId: boqData?.opportunity_id || undefined,
        selectedLineIds: selectedConvertLineIds,
        revenueServiceTypeId: revenueServiceTypeId ? String(revenueServiceTypeId).trim() : undefined,
        departmentId: convertDepartmentId.trim(),
      });
      const quotationId = ((res as { data?: { id?: string }; id?: string })?.data?.id ||
        (res as { id?: string })?.id) as string | undefined;
      message.success('Quotation created from selected BOQ lines.');
      setConvertModalOpen(false);
      if (quotationId) {
        navigate(`/growth/quotations/${quotationId}`);
      }
      await queryClient.invalidateQueries({ queryKey: ['quotations'] });
      await queryClient.refetchQueries({ queryKey: ['quotations', 'by-boq', id] });
    } catch (error: any) {
      message.error(error?.response?.data?.message || error?.message || 'Failed to create quotation');
    }
  };
  const { baseCurrency, allCurrencies } = useCurrency();

  const boqBreadcrumbSegments = useMemo(() => {
    if (!id) return null;
    const label =
      (boqData?.boq_number && String(boqData.boq_number).trim()) ||
      (boqData?.boq_name && String(boqData.boq_name).trim()) ||
      id;
    return [
      { title: 'BOQ Estimation', path: '/growth/estimations/boq' },
      { title: label, path: `/growth/estimations/boq/${id}` },
    ];
  }, [id, boqData?.boq_number, boqData?.boq_name]);

  useDocumentBreadcrumb(boqBreadcrumbSegments);
  const createBOQMutation = useCreateBOQ();
  const updateBOQMutation = useUpdateBOQ();
  const addLineItemsMutation = useAddLineItems();
  const updateLineItemMutation = useUpdateLineItem();
  const deleteLineItemMutation = useDeleteLineItem();
  const recalculateMutation = useRecalculateBOQ();
  const submitForReviewMutation = useSubmitBOQForReview();
  const approveBOQMutation = useApproveBOQ();
  const rejectBOQMutation = useRejectBOQ();

  // Materials & Operations mutations
  const addMaterialMutation = useAddBOQMaterial();
  const updateMaterialMutation = useUpdateBOQMaterial();
  const deleteMaterialMutation = useDeleteBOQMaterial();
  const addOperationMutation = useAddBOQOperation();
  const updateOperationMutation = useUpdateBOQOperation();
  const deleteOperationMutation = useDeleteBOQOperation();

  const { data: supplierCategoriesForOps = [], isLoading: loadingSupplierCategories } = useQuery({
    queryKey: ['procurement-supplier-categories'],
    queryFn: async () => {
      const res = await api.get('/procurement/supplier-categories');
      const rows = unwrapProcurementList<SupplierCategoryListRow[]>(res);
      return Array.isArray(rows) ? rows : [];
    },
  });

  const supplierCategorySelectOptions = useMemo(
    () =>
      supplierCategoriesForOps.map((c) => ({
        value: c.id,
        label: c.category_name,
      })),
    [supplierCategoriesForOps]
  );
  const toggleManualCostMutation = useToggleBOQManualCost();
  const updateMarginMutation = useUpdateBOQLineItemMargin();
  const recalculateLineItemMutation = useRecalculateBOQLineItem();
  const copyLineItemPricingMutation = useCopyBoqLineItemPricing();
  const { data: pricingHistoryRows = [], isFetching: pricingHistoryLoading } = useBoqLineItemPricingHistory(
    pricingHistoryQuery,
    {
      excludeLineItemId: currentLineItemId || undefined,
      limit: 30,
      enabled: pricingHistoryModalOpen
    }
  );

  const lineItemDrawerBusy =
    updateLineItemMutation.isPending ||
    addLineItemsMutation.isPending ||
    toggleManualCostMutation.isPending ||
    updateMarginMutation.isPending ||
    recalculateLineItemMutation.isPending ||
    copyLineItemPricingMutation.isPending;

  // Load existing BOQ data
  useEffect(() => {
    // Handle both direct BOQ object and wrapped response
    const boq = boqData;
    if (boq && boq.id) {

      // Set form values
      form.setFieldsValue({
        boqName: boq.boq_name,
        customerId: boq.customer_id,
        projectId: boq.project_id,
        projectName: boq.project_name,
        opportunityId: boq.opportunity_id,
        revenueServiceTypeId: boq.revenue_service_type_id || undefined,
        boqDate: boq.boq_date ? dayjs(boq.boq_date) : dayjs(),
        validUntilDate: boq.valid_until_date ? dayjs(boq.valid_until_date) : undefined,
        currency: boq.currency || 'USD',
        marginPercentage: boq.margin_percentage || 0,
        notes: boq.notes
      });

      // Organize line items by category
      const categoriesMap: Map<string, CategoryData> = new Map();

      // Add categorized items
      if (boq.categories && boq.categories.length > 0) {
        boq.categories.forEach(cat => {
          const items = boq.lineItems?.filter(item => item.category_id === cat.id) || [];
          categoriesMap.set(cat.id, {
            id: cat.id,
            name: cat.category_name,
            items: items.map(item => ({
              lineItemId: item.id,
              boqLineItem: item,
              categoryId: item.category_id,
              itemCode: item.item_code,
              itemName: item.item_name,
              description: item.description,
              revision: item.revision,
              quantity: item.quantity,
              uom: item.uom,
              unitCost: effectiveBoqLineUnitCost(item),
              costBasis: item.cost_basis,
              supplierName: item.supplier_name,
              leadTimeDays: item.lead_time_days,
              notes: item.notes,
              marginPercentage: item.margin_percentage
            }))
          });
        });
      }

      // Add uncategorized items
      const uncategorizedItems = boq.lineItems?.filter(item => !item.category_id) || [];
      if (uncategorizedItems.length > 0) {
        categoriesMap.set('uncategorized', {
          name: 'Uncategorized',
          items: uncategorizedItems.map(item => ({
            lineItemId: item.id,
            boqLineItem: item,
            itemCode: item.item_code,
            itemName: item.item_name,
            description: item.description,
            revision: item.revision,
            quantity: item.quantity,
            uom: item.uom,
            unitCost: item.use_manual_cost ? item.unit_cost : (item.calculated_unit_cost || item.unit_cost), // Use calculated cost if available
            costBasis: item.cost_basis,
            supplierName: item.supplier_name,
            leadTimeDays: item.lead_time_days,
            notes: item.notes,
            marginPercentage: item.margin_percentage
          }))
        });
      }

      if (categoriesMap.size > 0) {
        setCategories(Array.from(categoriesMap.values()));
        setActiveCategory('0');
      }
    }
  }, [boqData, form]);

  // Fetch operations master data
  useEffect(() => {
    const fetchOperations = async () => {
      try {
        setIsLoadingOperations(true);
        const userStr = localStorage.getItem('user');
        let companyId = '';
        
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            companyId = user.company_id || '';
          } catch (parseError) {
            console.warn('Failed to parse user from localStorage:', parseError);
          }
        }

        // Always try to fetch, backend will handle company_id fallback
        console.log('[BOQBuilderPage] Fetching operations with company_id:', companyId || 'empty (will use backend fallback)');
        const ops = await operationService.getAll(companyId, true); // Get only active operations
        console.log('[BOQBuilderPage] Fetched operations:', ops?.length || 0);
        console.log('[BOQBuilderPage] Operations data:', ops);
        
        if (Array.isArray(ops)) {
          setOperations(ops);
          if (ops.length === 0) {
            console.warn('[BOQBuilderPage] No operations found. Make sure operations are created in Manufacturing > Operations Master.');
          }
        } else {
          console.warn('[BOQBuilderPage] Operations response is not an array:', ops);
          setOperations([]);
        }
      } catch (error: any) {
        console.error('[BOQBuilderPage] Failed to fetch operations:', error);
        console.error('[BOQBuilderPage] Error response:', error.response?.data);
        message.error(error.response?.data?.message || 'Failed to load operations master data');
        setOperations([]);
      } finally {
        setIsLoadingOperations(false);
      }
    };

    fetchOperations();
  }, []);

  // Debug: Log when operations change
  useEffect(() => {
    console.log('[BOQBuilderPage] Operations state changed:', {
      count: operations.length,
      operations: operations.map(op => ({ id: op.id, code: op.operation_code, name: op.operation_name }))
    });
  }, [operations]);

  // Calculate totals (per-line selling uses persisted line_selling_total or margin-adjusted fallback)
  const calculateTotals = () => {
    const boqMargin = Number(watchedBoqMargin ?? form.getFieldValue('marginPercentage') ?? 0);
    let grandCostTotal = 0;
    let grandSellingTotal = 0;
    const categoryCostTotals: number[] = [];
    const categorySellingTotals: number[] = [];

    categories.forEach((category) => {
      let catCost = 0;
      let catSell = 0;
      category.items.forEach((item) => {
        catCost += getLineCostTotalForLineItem(item as LineItemRow);
        catSell += getLineSellingTotalForLineItem(item as LineItemRow, boqMargin);
      });
      categoryCostTotals.push(catCost);
      categorySellingTotals.push(catSell);
      grandCostTotal += catCost;
      grandSellingTotal += catSell;
    });

    const marginAmount = grandSellingTotal - grandCostTotal;

    return {
      grandCostTotal,
      grandSellingTotal,
      marginAmount,
      categoryCostTotals,
      categorySellingTotals
    };
  };

  const { grandCostTotal, grandSellingTotal, marginAmount, categoryCostTotals, categorySellingTotals } =
    calculateTotals();
  
  // Get currency from form
  const currency = form.getFieldValue('currency') || 'USD';
  const currencySymbol = currency === 'AED' ? 'AED' : currency === 'USD' ? '$' : currency;

  const openPricingHistoryModal = () => {
    if (!id || !currentLineItemId) {
      message.warning('Save the line item first to search pricing from history.');
      return;
    }
    const n = (itemForm.getFieldValue('itemName') as string) || '';
    setHistorySearchText(n);
    setPricingHistoryQuery(n.trim().length >= 2 ? n.trim() : '');
    setPricingHistoryModalOpen(true);
  };

  const applyPricingFromHistory = useCallback(
    (row: BoqLineItemPricingHistoryRow) => {
      if (!id || !currentLineItemId) return;
      Modal.confirm({
        title: 'Replace pricing from history?',
        content: `This replaces all materials, operations, and technical documents on this line and overwrites cost mode, margin, and supplier fields from ${row.boq_number} — "${row.item_name}". This line's item name, code, quantity, and UOM stay unchanged.`,
        okText: 'Copy pricing',
        okType: 'danger',
        onOk: async () => {
          await copyLineItemPricingMutation.mutateAsync({
            boqId: id,
            lineItemId: currentLineItemId,
            sourceLineItemId: row.line_item_id
          });
          await patchLineItemDrawerAfterBoqRefetch(currentLineItemId);
          setPricingHistoryModalOpen(false);
          setActiveTab('materials');
          message.success('Pricing and documents copied from history');
        }
      });
    },
    [id, currentLineItemId, copyLineItemPricingMutation, patchLineItemDrawerAfterBoqRefetch]
  );

  const pricingHistoryColumns: ColumnsType<BoqLineItemPricingHistoryRow> = useMemo(
    () => [
      { title: 'BOQ', dataIndex: 'boq_number', key: 'boq_number', width: 100 },
      {
        title: 'Item',
        key: 'item',
        ellipsis: true,
        render: (_: unknown, r) => {
          const code = String(r.item_code ?? '').trim();
          return code ? `${code} — ${r.item_name}` : r.item_name;
        },
      },
      {
        title: `Unit / calc (${currencySymbol})`,
        key: 'costs',
        width: 150,
        render: (_: unknown, r) => (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.use_manual_cost ? Number(r.unit_cost).toFixed(2) : Number(r.calculated_unit_cost).toFixed(2)}
            {r.use_manual_cost ? ' m' : ' a'}
          </Text>
        )
      },
      { title: 'Mat', dataIndex: 'material_count', key: 'mat', width: 48 },
      { title: 'Ops', dataIndex: 'operation_count', key: 'ops', width: 48 },
      {
        title: '',
        key: 'act',
        width: 100,
        render: (_: unknown, r) => (
          <Button
            type="primary"
            size="small"
            onClick={() => applyPricingFromHistory(r)}
            disabled={lineItemDrawerBusy}
          >
            Use
          </Button>
        )
      }
    ],
    [applyPricingFromHistory, currencySymbol, lineItemDrawerBusy]
  );

  // Handlers
  const handleAddCategory = () => {
    setCategoryModalVisible(true);
    setNewCategoryName('');
  };

  const handleCategoryModalOk = () => {
    if (!newCategoryName.trim()) {
      message.error('Category name cannot be empty');
      return;
    }

    setCategories([...categories, { name: newCategoryName, items: [] }]);
    setActiveCategory(categories.length.toString());
    setCategoryModalVisible(false);
  };

  const handleDeleteCategory = (index: number) => {
    if (categories.length === 1) {
      message.error('Cannot delete the last category');
      return;
    }

    const newCategories = categories.filter((_, i) => i !== index);
    setCategories(newCategories);

    // Adjust active category
    const activeIndex = parseInt(activeCategory);
    if (activeIndex >= index) {
      setActiveCategory(Math.max(0, activeIndex - 1).toString());
    }
  };

  const handleAddItem = () => {
    setEditingItem(null);
    itemForm.resetFields();
    itemForm.setFieldsValue({
      costBasis: 'Estimated',
      leadTimeDays: 0
    });
    setItemModalVisible(true);
  };

  const handleEditItem = (categoryIndex: number, itemIndex: number, lineItemId?: string) => {
    const item = categories[categoryIndex].items[itemIndex];
    setEditingItem({ 
      categoryIndex, 
      itemIndex, 
      lineItemId,
      boqLineItem: item.boqLineItem 
    });
    setCurrentLineItemId(lineItemId || null);
    itemForm.setFieldsValue(item);
    setActiveTab('basic');
    setItemModalVisible(true);
  };

  const handleDeleteItem = async (categoryIndex: number, itemIndex: number) => {
    const row = categories[categoryIndex]?.items[itemIndex];
    if (!row) return;

    const persistedId = row.lineItemId ?? row.boqLineItem?.id;

    // Persisted BOQ line: must delete on server — header-only update does not sync line items, so refetch would bring rows back.
    if (id && persistedId) {
      try {
        await deleteLineItemMutation.mutateAsync({ boqId: id, itemId: persistedId });
        if (currentLineItemId === persistedId) {
          setCurrentLineItemId(null);
          setItemModalVisible(false);
          setEditingItem(null);
        }
      } catch {
        return;
      }
      return;
    }

    const newCategories = [...categories];
    newCategories[categoryIndex].items.splice(itemIndex, 1);
    setCategories(newCategories);
    message.success('Item removed');
  };

  const handleItemModalOk = async () => {
    try {
      const values = await itemForm.validateFields();

      const newItem: LineItemRequest = {
        ...values,
        categoryName: categories[parseInt(activeCategory)].name
      };

      // If editing an existing item with a lineItemId (already saved to DB), update via API
      if (editingItem !== null && editingItem.lineItemId && id) {
        await updateLineItemMutation.mutateAsync({
          boqId: id,
          itemId: editingItem.lineItemId,
          data: newItem
        });
        await queryClient.refetchQueries({ queryKey: BOQ_KEYS.detail(id) });
        setItemModalVisible(false);
        setCurrentLineItemId(null);
        return;
      }

      const categoryIndex = parseInt(activeCategory);

      // Saved BOQ: header Save does not sync line items, so a new line must be persisted here or it is lost on refresh.
      if (id && !editingItem?.lineItemId) {
        const targetCategory =
          editingItem !== null ? categories[editingItem.categoryIndex] : categories[categoryIndex];
        const { categoryName: _localName, ...itemFields } = newItem;
        const payload: LineItemRequest = targetCategory?.id
          ? { ...itemFields, categoryId: targetCategory.id }
          : targetCategory && targetCategory.name !== 'Uncategorized'
            ? { ...itemFields, categoryName: targetCategory.name }
            : itemFields;
        await addLineItemsMutation.mutateAsync({ boqId: id, items: [payload] });
        await queryClient.refetchQueries({ queryKey: BOQ_KEYS.detail(id) });
        setItemModalVisible(false);
        setEditingItem(null);
        setCurrentLineItemId(null);
        return;
      }

      // New (unsaved) BOQ: keep in local state until the BOQ is created
      const newCategories = [...categories];

      if (editingItem !== null) {
        // Update existing item in local state
        newCategories[editingItem.categoryIndex].items[editingItem.itemIndex] = newItem;
      } else {
        // Add new item to local state
        newCategories[categoryIndex].items.push(newItem);
      }

      setCategories(newCategories);
      setItemModalVisible(false);
      setCurrentLineItemId(null);
      message.success(editingItem ? 'Item updated' : 'Item added');
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  // Material Handlers
  const handleAddMaterial = () => {
    setEditingMaterial(null);
    setSelectedItem(null);
    materialForm.resetFields();
    materialForm.setFieldsValue({
      linkMode: 'inventory',
      costBasis: 'Estimated',
      quantity: 1
    });
    setMaterialModalVisible(true);
  };

  const rememberItem = (item: Item) =>
    setItems((prev) => (prev.some((i) => i.id === item.id) ? prev : [...prev, item]));

  const handleItemSelect = async (itemId: string, itemOverride?: Item | null) => {
    let item = itemOverride ?? items.find((i) => i.id === itemId) ?? null;
    if (!item) {
      try {
        item = await inventoryService.getItemById(itemId);
        if (item) rememberItem(item);
      } catch (error: any) {
        message.error(error?.response?.data?.message || 'Could not load the selected item');
      }
    }
    if (item) {
      setSelectedItem(item);
      const lastPurchase = Number(item.last_purchase_price) || 0;
      const masterPurchase = Number(item.standard_cost) || 0;
      const unitCost = lastPurchase || masterPurchase;
      materialForm.setFieldsValue({
        item_id: item.id,
        description: item.item_name + (item.item_description ? ' - ' + item.item_description : ''),
        uom: item.base_uom || 'pcs',
        unitCost,
        costBasis: lastPurchase > 0 ? 'Last Purchase' : masterPurchase > 0 ? 'Standard Cost' : 'Estimated',
      });
    }
  };

  const handleEditMaterial = (material: BOQLineItemMaterial) => {
    setEditingMaterial(material);
    const linked = Boolean(String(material.material_id ?? '').trim());
    setSelectedItem(linked ? ({} as Item) : null);
    if (linked && !items.some((i) => i.id === material.material_id)) {
      inventoryService
        .getItemById(String(material.material_id))
        .then((item) => item && rememberItem(item))
        .catch(() => undefined);
    }
    materialForm.setFieldsValue({
      linkMode: linked ? 'inventory' : 'description',
      item_id: material.material_id || undefined,
      description: material.description,
      quantity: material.quantity,
      uom: material.uom,
      unitCost: material.unit_cost,
      costBasis: material.cost_basis,
      supplierName: material.supplier_name,
      notes: material.notes
    });
    setMaterialModalVisible(true);
  };

  const handleMaterialModalOk = async () => {
    if (!id || !currentLineItemId) {
      message.error('Please save the BOQ and line item first');
      return;
    }
    if (materialModalSaveLockRef.current) {
      return;
    }
    materialModalSaveLockRef.current = true;
    setMaterialModalSaving(true);

    try {
      const values = await materialForm.validateFields();
      const linkMode = values.linkMode === 'description' ? 'description' : 'inventory';
      const materialData: MaterialRequest = {
        materialId: linkMode === 'inventory' ? values.item_id : undefined,
        description: values.description,
        quantity: values.quantity,
        uom: values.uom,
        unitCost: values.unitCost,
        costBasis: values.costBasis,
        supplierName: values.supplierName,
        notes: values.notes
      };

      if (editingMaterial) {
        await updateMaterialMutation.mutateAsync({
          boqId: id,
          lineItemId: currentLineItemId,
          materialId: editingMaterial.id,
          material: {
            ...materialData,
            // Explicitly clear inventory link when switching to description-only.
            materialId: linkMode === 'inventory' ? values.item_id : null,
          } as MaterialRequest,
        });
      } else {
        await addMaterialMutation.mutateAsync({
          boqId: id,
          lineItemId: currentLineItemId,
          material: materialData
        });
      }

      setMaterialModalVisible(false);
      materialForm.resetFields();
    } catch (error) {
      if (error && typeof error === 'object' && 'errorFields' in error) {
        // validation
      } else {
        console.error('Material save failed:', error);
      }
    } finally {
      materialModalSaveLockRef.current = false;
      setMaterialModalSaving(false);
    }
  };

  const handleDeleteMaterial = async (materialId: string) => {
    if (!id || !currentLineItemId) return;

    try {
      await deleteMaterialMutation.mutateAsync({
        boqId: id,
        lineItemId: currentLineItemId,
        materialId
      });
    } catch (error) {
      console.error('Material delete failed:', error);
    }
  };

  // Operation Handlers
  const handleAddOperation = () => {
    setEditingOperation(null);
    operationForm.resetFields();
    operationForm.setFieldsValue({
      sequenceNumber: 1,
      setupTimeHours: 0,
      runTimePerUnitHours: 0,
      laborRatePerHour: 0,
      overheadRatePerHour: 0,
      machineRatePerHour: 0,
      toolingCost: 0,
      isOutsourced: false,
      outsourcedSupplierCategoryIds: [],
    });
    setOperationModalVisible(true);
  };

  const handleEditOperation = (operation: BOQLineItemOperation) => {
    setEditingOperation(operation);
    operationForm.setFieldsValue({
      operationId: operation.operation_id, // Set the selected operation ID
      description: operation.description,
      sequenceNumber: operation.sequence_number,
      setupTimeHours: operation.setup_time_hours,
      runTimePerUnitHours: operation.run_time_per_unit_hours,
      laborRatePerHour: operation.labor_rate_per_hour,
      overheadRatePerHour: operation.overhead_rate_per_hour,
      machineRatePerHour: operation.machine_rate_per_hour,
      toolingCost: operation.tooling_cost,
      isOutsourced: operation.is_outsourced,
      outsourceCost: operation.outsource_cost,
      outsourcedSupplierCategoryIds: operation.outsourced_supplier_category_ids ?? [],
      outsourcedScopeDetail: operation.outsourced_scope_detail ?? undefined,
      notes: operation.notes
    });
    setOperationModalVisible(true);
  };

  const handleOperationModalOk = async () => {
    if (!id || !currentLineItemId) {
      message.error('Please save the BOQ and line item first');
      return;
    }
    if (operationModalSaveLockRef.current) {
      return;
    }
    operationModalSaveLockRef.current = true;
    setOperationModalSaving(true);

    try {
      const values = await operationForm.validateFields();

      const isOutsourced = Boolean(values.isOutsourced);
      const operationData: OperationRequest = {
        operationId: values.operationId,
        description: values.description && String(values.description).trim() ? String(values.description).trim() : (isOutsourced ? 'Operation outsourced' : 'Operation'),
        sequenceNumber: values.sequenceNumber,
        setupTimeHours: values.setupTimeHours,
        runTimePerUnitHours: values.runTimePerUnitHours,
        laborRatePerHour: values.laborRatePerHour,
        overheadRatePerHour: values.overheadRatePerHour,
        machineRatePerHour: values.machineRatePerHour,
        toolingCost: values.toolingCost,
        isOutsourced,
        outsourceCost: isOutsourced ? Number(values.outsourceCost) : undefined,
        outsourcedSupplierCategoryIds: isOutsourced
          ? Array.isArray(values.outsourcedSupplierCategoryIds)
            ? (values.outsourcedSupplierCategoryIds as string[]).filter((x) => typeof x === 'string' && x.trim())
            : []
          : undefined,
        outsourcedScopeDetail: isOutsourced
          ? (typeof values.outsourcedScopeDetail === 'string' ? values.outsourcedScopeDetail.trim() : '') || null
          : null,
        notes: values.notes
      };

      if (editingOperation) {
        await updateOperationMutation.mutateAsync({
          boqId: id,
          lineItemId: currentLineItemId,
          operationId: editingOperation.id,
          operation: operationData
        });
      } else {
        await addOperationMutation.mutateAsync({
          boqId: id,
          lineItemId: currentLineItemId,
          operation: operationData
        });
      }

      setOperationModalVisible(false);
      setEditingOperation(null);
      operationForm.resetFields();
    } catch (error) {
      if (error && typeof error === 'object' && 'errorFields' in error) {
        // Form validation — no toast
      } else {
        console.error('Operation save failed:', error);
      }
    } finally {
      operationModalSaveLockRef.current = false;
      setOperationModalSaving(false);
    }
  };

  const handleOperationModalCancel = () => {
    if (operationModalSaving) {
      return;
    }
    setOperationModalVisible(false);
    setEditingOperation(null);
    operationForm.resetFields();
  };

  const handleDeleteOperation = async (operationId: string) => {
    if (!id || !currentLineItemId) return;

    try {
      await deleteOperationMutation.mutateAsync({
        boqId: id,
        lineItemId: currentLineItemId,
        operationId
      });
    } catch (error) {
      console.error('Operation delete failed:', error);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      // Get customer name from customerId
      const customerName = values.customerId
        ? customers?.find(c => c.id === values.customerId)?.customer_name
        : undefined;

      const request: BOQRequest = {
        boqName: values.boqName,
        customerId: values.customerId,
        customerName: customerName,
        projectName: values.projectName,
        projectId: values.projectId,
        boqDate: values.boqDate ? values.boqDate.format('YYYY-MM-DD') : undefined,
        validUntilDate: values.validUntilDate ? values.validUntilDate.format('YYYY-MM-DD') : undefined,
        currency: values.currency || 'USD',
        marginPercentage: values.marginPercentage || 0,
        notes: values.notes,
        categories: categories.map(cat => ({
          categoryName: cat.name,
          lineItems: cat.items
        }))
      };

      if (values.opportunityId?.trim()) {
        request.opportunityId = values.opportunityId.trim();
      } else if (id) {
        request.opportunityId = null;
      }

      request.revenueServiceTypeId = values.revenueServiceTypeId?.trim()
        ? String(values.revenueServiceTypeId).trim()
        : null;

      if (id) {
        await updateBOQMutation.mutateAsync({ id, data: request });
      } else {
        const result = await createBOQMutation.mutateAsync(request);
        // Handle both direct response and wrapped response
        const createdBoq = result?.data || result;
        if (createdBoq?.id) {
          navigate(`/growth/estimations/boq/edit/${createdBoq.id}`);
        }
      }
    } catch (error) {
      console.error('Save failed:', error);
    }
  };

  const handleSubmitForReview = () => {
    if (!id || !boqData) return;
    Modal.confirm({
      title: 'Submit for Review',
      content: `Are you sure you want to submit "${boqData.boq_name}" for review?`,
      onOk: async () => {
        await submitForReviewMutation.mutateAsync(id);
        await queryClient.invalidateQueries({ queryKey: BOQ_KEYS.detail(id) });
      }
    });
  };

  const handleApproveBOQ = () => {
    if (!id || !boqData) return;
    Modal.confirm({
      title: 'Approve BOQ',
      content: `Are you sure you want to approve "${boqData.boq_name}"?`,
      onOk: async () => {
        await approveBOQMutation.mutateAsync(id);
        await queryClient.invalidateQueries({ queryKey: BOQ_KEYS.detail(id) });
      }
    });
  };

  const handleRejectBOQ = () => {
    if (!id || !boqData) return;
    let rejectionReason = '';
    Modal.confirm({
      title: 'Reject BOQ',
      content: (
        <div>
          <p>Are you sure you want to reject &quot;{boqData.boq_name}&quot;?</p>
          <Input.TextArea
            placeholder="Please provide a reason for rejection (optional)"
            rows={3}
            onChange={(e) => {
              rejectionReason = e.target.value;
            }}
          />
        </div>
      ),
      onOk: async () => {
        await rejectBOQMutation.mutateAsync({ id, reason: rejectionReason || undefined });
        await queryClient.invalidateQueries({ queryKey: BOQ_KEYS.detail(id) });
      }
    });
  };

  const tableBoqMargin = Number(watchedBoqMargin ?? form.getFieldValue('marginPercentage') ?? 0);

  // Table columns for line items
  const itemColumns: ColumnsType<LineItemRequest> = [
    {
      title: '#',
      width: 50,
      render: (_, __, index) => index + 1
    },
    {
      title: 'Item Code',
      dataIndex: 'itemCode',
      key: 'itemCode',
      width: 120
    },
    {
      title: 'Item Name',
      dataIndex: 'itemName',
      key: 'itemName',
      width: 200
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: 'Revision',
      dataIndex: 'revision',
      key: 'revision',
      width: 90,
      render: (v: string) => v || '—'
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'right'
    },
    {
      title: 'UOM',
      dataIndex: 'uom',
      key: 'uom',
      width: 80
    },
    {
      title: 'Unit Cost (basis)',
      dataIndex: 'unitCost',
      key: 'unitCost',
      width: 130,
      align: 'right',
      render: (value: number) => `${currencySymbol} ${Number(value || 0).toFixed(2)}`
    },
    {
      title: 'Line Cost',
      key: 'lineCost',
      width: 120,
      align: 'right',
      render: (_, record) =>
        `${currencySymbol} ${getLineCostTotalForLineItem(record as LineItemRow).toFixed(2)}`
    },
    {
      title: 'Unit Selling',
      key: 'unitSelling',
      width: 120,
      align: 'right',
      render: (_, record) =>
        `${currencySymbol} ${getUnitSellingPriceForLineItem(record as LineItemRow, tableBoqMargin).toFixed(2)}`
    },
    {
      title: 'Line Selling',
      key: 'lineSelling',
      width: 120,
      align: 'right',
      render: (_, record) => (
        <Text strong style={{ color: '#52c41a' }}>
          {currencySymbol}{' '}
          {getLineSellingTotalForLineItem(record as LineItemRow, tableBoqMargin).toFixed(2)}
        </Text>
      )
    },
    {
      title: 'Margin %',
      key: 'margin',
      width: 100,
      align: 'center',
      render: (_, record) => {
        const row = record as LineItemRow;
        if (row.boqLineItem) {
          const margin = row.boqLineItem.margin_percentage;
          if (margin !== undefined && margin !== null) {
            return <Tag color="blue">{margin}%</Tag>;
          }
        }
        if (row.marginPercentage !== undefined && row.marginPercentage !== null) {
          return <Tag color="blue">{row.marginPercentage}%</Tag>;
        }
        return <Tag>{tableBoqMargin}%</Tag>;
      }
    },
    {
      title: 'Cost Mode',
      key: 'costMode',
      width: 120,
      align: 'center',
      render: (_, record) => {
        if (!record.boqLineItem) return '-';
        const hasBreakdown = lineItemBreakdownTotal(record.boqLineItem) > 0;
        const useManual = record.boqLineItem.use_manual_cost && !hasBreakdown;
        if (hasBreakdown) {
          return <Badge status="processing" text="Calculated" />;
        }
        if (useManual) {
          return <Badge status="default" text="Manual" />;
        }
        return <Badge status="default" text="Manual" />;
      }
    },
    {
      title: 'Details',
      key: 'details',
      width: 120,
      align: 'center',
      render: (_, record) => {
        if (!record.boqLineItem) return '-';
        const materialCount = record.boqLineItem.materials?.length || 0;
        const operationCount = record.boqLineItem.operations?.length || 0;
        return (
          <Space size={4}>
            {materialCount > 0 && <Tag icon={<ToolOutlined />} color="blue">{materialCount} Mat</Tag>}
            {operationCount > 0 && <Tag icon={<ToolOutlined />} color="green">{operationCount} Op</Tag>}
            {materialCount === 0 && operationCount === 0 && '-'}
          </Space>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record, index) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditItem(parseInt(activeCategory), index, record.lineItemId)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this item?"
            onConfirm={() => handleDeleteItem(parseInt(activeCategory), index)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  if (isLoadingBOQ) {
    return (
      <div style={{ padding: '24px' }}>
        <Card loading />
      </div>
    );
  }

  // ===== Tab Content Components =====

  // Materials Tab Component
  const MaterialsTabContent: React.FC<{
    boqId: string;
    lineItemId: string;
    currencySymbol: string;
    onAddMaterial: () => void;
    onEditMaterial: (material: BOQLineItemMaterial) => void;
    onDeleteMaterial: (materialId: string) => void;
  }> = ({ boqId, lineItemId, currencySymbol, onAddMaterial, onEditMaterial, onDeleteMaterial }) => {
    const { data: materials, isLoading } = useBOQMaterials(boqId, lineItemId);

    const materialColumns: ColumnsType<BOQLineItemMaterial> = [
      {
        title: 'Inventory',
        key: 'inventory_link',
        width: 110,
        render: (_, record) =>
          String(record.material_id ?? '').trim() ? (
            <Tag color="green">Linked</Tag>
          ) : (
            <Tag color="orange">Unlinked</Tag>
          ),
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        width: 200
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
        key: 'quantity',
        width: 100,
        align: 'right',
        render: (val) => Number(val || 0).toFixed(2)
      },
      {
        title: 'UOM',
        dataIndex: 'uom',
        key: 'uom',
        width: 80
      },
      {
        title: 'Unit Cost',
        dataIndex: 'unit_cost',
        key: 'unit_cost',
        width: 120,
        align: 'right',
        render: (val) => `${currencySymbol} ${Number(val || 0).toFixed(2)}`
      },
      {
        title: 'Total Cost',
        dataIndex: 'total_cost',
        key: 'total_cost',
        width: 120,
        align: 'right',
        render: (val) => `${currencySymbol} ${Number(val || 0).toFixed(2)}`
      },
      {
        title: 'Cost Basis',
        dataIndex: 'cost_basis',
        key: 'cost_basis',
        width: 120
      },
      {
        title: 'Supplier',
        dataIndex: 'supplier_name',
        key: 'supplier_name',
        width: 150,
        ellipsis: true
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 100,
        fixed: 'right',
        render: (_, record) => (
          <Space>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => onEditMaterial(record)}
            />
            <Popconfirm
              title="Delete this material?"
              onConfirm={() => onDeleteMaterial(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        )
      }
    ];

    return (
      <div>
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={onAddMaterial}>
            Add Material
          </Button>
        </div>
        <Table
          dataSource={materials || []}
          columns={materialColumns}
          rowKey="id"
          loading={isLoading}
          pagination={false}
          size="small"
          scroll={{ x: 900 }}
        />
        {(!materials || materials.length === 0) && !isLoading && (
          <Alert
            message="No Materials"
            description="Add materials to calculate costs automatically."
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </div>
    );
  };

  // Operations Tab Component
  const OperationsTabContent: React.FC<{
    boqId: string;
    lineItemId: string;
    currencySymbol: string;
    onAddOperation: () => void;
    onEditOperation: (operation: BOQLineItemOperation) => void;
    onDeleteOperation: (operationId: string) => void;
  }> = ({ boqId, lineItemId, currencySymbol, onAddOperation, onEditOperation, onDeleteOperation }) => {
    const { data: operations, isLoading } = useBOQOperations(boqId, lineItemId);

    const operationColumns: ColumnsType<BOQLineItemOperation> = [
      {
        title: 'Seq',
        dataIndex: 'sequence_number',
        key: 'sequence_number',
        width: 60,
        align: 'center'
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        width: 200
      },
      {
        title: 'Setup (hrs)',
        dataIndex: 'setup_time_hours',
        key: 'setup_time_hours',
        width: 100,
        align: 'right',
        render: (val) => Number(val || 0).toFixed(2)
      },
      {
        title: 'Run (hrs)',
        dataIndex: 'run_time_per_unit_hours',
        key: 'run_time_per_unit_hours',
        width: 120,
        align: 'right',
        render: (val) => Number(val || 0).toFixed(4)
      },
      {
        title: 'Labor Rate',
        dataIndex: 'labor_rate_per_hour',
        key: 'labor_rate_per_hour',
        width: 100,
        align: 'right',
        render: (val) => `${Number(val || 0).toFixed(0)}`
      },
      {
        title: 'Overhead',
        dataIndex: 'overhead_rate_per_hour',
        key: 'overhead_rate_per_hour',
        width: 90,
        align: 'right',
        render: (val) => `${Number(val || 0).toFixed(0)}`
      },
      {
        title: 'Machine',
        dataIndex: 'machine_rate_per_hour',
        key: 'machine_rate_per_hour',
        width: 90,
        align: 'right',
        render: (val) => `${Number(val || 0).toFixed(0)}`
      },
      {
        title: 'Material Cost',
        dataIndex: 'tooling_cost',
        key: 'tooling_cost',
        width: 80,
        align: 'right',
        render: (val) => `${Number(val || 0).toFixed(2)}`
      },
      {
        title: 'Total Cost',
        dataIndex: 'total_cost',
        key: 'total_cost',
        width: 110,
        align: 'right',
        render: (val) => `${currencySymbol} ${Number(val || 0).toFixed(2)}`
      },
      {
        title: 'Type',
        key: 'type',
        width: 100,
        render: (_, record) => record.is_outsourced ? <Tag color="orange">Outsourced</Tag> : <Tag color="blue">In-house</Tag>
      },
      {
        title: 'Outsourced scope',
        key: 'outsourced_scope',
        width: 160,
        ellipsis: true,
        render: (_, record) => {
          if (!record.is_outsourced) return '—';
          const scope = record.outsourced_scope_detail?.trim();
          return scope ? scope : '—';
        },
      },
      {
        title: 'Supp. categories',
        key: 'supp_cat',
        width: 110,
        align: 'center',
        render: (_, record) => {
          if (!record.is_outsourced) return '—';
          const n = record.outsourced_supplier_category_ids?.length ?? 0;
          return n > 0 ? n : '—';
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 100,
        fixed: 'right',
        render: (_, record) => (
          <Space>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => onEditOperation(record)}
            />
            <Popconfirm
              title="Delete this operation?"
              onConfirm={() => onDeleteOperation(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        )
      }
    ];

    return (
      <div>
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={onAddOperation}>
            Add Operation
          </Button>
        </div>
        <Table
          dataSource={operations || []}
          columns={operationColumns}
          rowKey="id"
          loading={isLoading}
          pagination={false}
          size="small"
          scroll={{ x: 1000 }}
        />
        {(!operations || operations.length === 0) && !isLoading && (
          <Alert
            message="No Operations"
            description="Add operations to calculate labor and overhead costs."
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </div>
    );
  };

  // Cost Summary Tab Component
  const CostSummaryTabContent: React.FC<{
    boqId: string;
    lineItemId: string;
    lineItem?: BOQLineItem;
    currencySymbol: string;
    boqMargin: number;
    onToggleManualCost: (useManual: boolean) => void;
    onUpdateMargin: (margin: number | null) => void;
    onRecalculate: () => void;
    lineItemDrawerBusy: boolean;
    manualCostLoading: boolean;
    marginActionLoading: boolean;
    recalculateLoading: boolean;
  }> = ({
    boqId,
    lineItemId,
    lineItem,
    currencySymbol,
    boqMargin,
    onToggleManualCost,
    onUpdateMargin,
    onRecalculate,
    lineItemDrawerBusy,
    manualCostLoading,
    marginActionLoading,
    recalculateLoading
  }) => {
    // Fetch fresh BOQ data to get the latest line item info
    const { data: boqData } = useBOQ(boqId);
    const boq = boqData;
    
    // Prefer server-fresh line from BOQ query; stale editingItem.boqLineItem was masking toggles/margin until save
    const currentLineItem =
      boq?.lineItems?.find((li: BOQLineItem) => li.id === lineItemId) ?? lineItem;

    const pricingContext = useMemo(() => {
      if (!currentLineItem) {
        return {
          basisUnit: 0,
          sellingCostBasis: 0,
          hasCostBreakdown: false,
          useManualCost: false,
          totalCost: 0,
          correctCalculatedUnitCost: 0,
          operationOutsourcedCost: 0,
        };
      }
      const operationOutsourcedCost = Number(currentLineItem.operation_outsourced_cost ?? 0);
      const totalCost =
        Number(currentLineItem.material_cost || 0) +
        Number(currentLineItem.labor_cost || 0) +
        Number(currentLineItem.overhead_cost || 0) +
        Number(currentLineItem.machine_cost || 0) +
        Number(currentLineItem.tooling_cost || 0) +
        operationOutsourcedCost;
      const qty = Number(currentLineItem.quantity) || 0;
      // Materials/ops sums are unit costs — do not divide by line qty.
      const correctCalculatedUnitCost = totalCost;
      const hasCostBreakdown = totalCost > 0;
      const manualUnit = Number(currentLineItem.unit_cost) || 0;
      const useManualCost = !hasCostBreakdown && manualUnit > 0;
      const basisUnit = hasCostBreakdown ? correctCalculatedUnitCost : manualUnit;
      const sellingCostBasis = basisUnit;
      return {
        basisUnit,
        sellingCostBasis,
        hasCostBreakdown,
        useManualCost,
        totalCost: basisUnit * qty,
        correctCalculatedUnitCost,
        operationOutsourcedCost,
      };
    }, [currentLineItem]);

    const [marginInput, setMarginInput] = useState<number | null>(null);
    const [sellingPriceInput, setSellingPriceInput] = useState<number | null>(null);

    useEffect(() => {
      if (!currentLineItem) return;
      const margin =
        currentLineItem.margin_percentage !== null && currentLineItem.margin_percentage !== undefined
          ? Number(currentLineItem.margin_percentage)
          : null;
      setMarginInput(margin);
      const serverSelling = Number(currentLineItem.unit_selling_price);
      if (Number.isFinite(serverSelling) && serverSelling > 0) {
        setSellingPriceInput(roundBoqMoney(serverSelling));
      } else {
        const eff = margin ?? boqMargin;
        setSellingPriceInput(
          pricingContext.sellingCostBasis > 0
            ? roundBoqMoney(sellingFromMargin(pricingContext.sellingCostBasis, Number(eff) || 0))
            : null
        );
      }
    }, [
      currentLineItem?.id,
      currentLineItem?.margin_percentage,
      currentLineItem?.unit_selling_price,
      pricingContext.sellingCostBasis,
      boqMargin,
    ]);

    const handleMarginInputChange = (value: number | null) => {
      setMarginInput(value);
      if (value !== null && value !== undefined && pricingContext.sellingCostBasis > 0) {
        setSellingPriceInput(roundBoqMoney(sellingFromMargin(pricingContext.sellingCostBasis, value)));
      }
    };

    const handleSellingPriceInputChange = (value: number | null) => {
      setSellingPriceInput(value);
      if (value !== null && value !== undefined && pricingContext.sellingCostBasis > 0) {
        const derived = marginFromSelling(pricingContext.sellingCostBasis, value);
        if (derived !== null) {
          setMarginInput(roundBoqMarginPct(derived));
        }
      }
    };

    const handleUpdateFromSellingPrice = () => {
      if (pricingContext.sellingCostBasis <= 0) {
        message.error('Line cost must be greater than zero to set selling price');
        return;
      }
      if (sellingPriceInput === null || sellingPriceInput === undefined || sellingPriceInput < 0) {
        message.error('Enter a valid unit selling price');
        return;
      }
      const derived = marginFromSelling(pricingContext.sellingCostBasis, sellingPriceInput);
      if (derived === null) {
        message.error('Line cost must be greater than zero to set selling price');
        return;
      }
      onUpdateMargin(roundBoqMarginPct(derived));
    };

    const handleClearMargin = () => {
      setMarginInput(null);
      setSellingPriceInput(null);
      onUpdateMargin(null);
    };

    if (!currentLineItem) {
      return (
        <Alert
          message="No Data"
          description="Line item data not available. Please save the line item first."
          type="warning"
          showIcon
        />
      );
    }

    const {
      basisUnit,
      sellingCostBasis,
      hasCostBreakdown,
      useManualCost,
      totalCost,
      correctCalculatedUnitCost,
      operationOutsourcedCost,
    } = pricingContext;

    const previewEffectiveMargin = Number(marginInput ?? boqMargin) || 0;
    const previewUnitSelling =
      sellingPriceInput ?? sellingFromMargin(sellingCostBasis, previewEffectiveMargin);
    const previewLineSelling = previewUnitSelling * Number(currentLineItem.quantity);
    const previewMarginPerUnit = previewUnitSelling - sellingCostBasis;

    return (
      <div>
        <Card title="Cost Breakdown" size="small" style={{ marginBottom: 16 }}>
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Material Cost">
              {currencySymbol} {Number(currentLineItem.material_cost || 0).toFixed(2)}
            </Descriptions.Item>
            <Descriptions.Item label="Labor Cost">
              {currencySymbol} {Number(currentLineItem.labor_cost || 0).toFixed(2)}
            </Descriptions.Item>
            <Descriptions.Item label="Overhead Cost">
              {currencySymbol} {Number(currentLineItem.overhead_cost || 0).toFixed(2)}
            </Descriptions.Item>
            <Descriptions.Item label="Machine Cost">
              {currencySymbol} {Number(currentLineItem.machine_cost || 0).toFixed(2)}
            </Descriptions.Item>
            <Descriptions.Item label="Material cost (operations)">
              {currencySymbol} {Number(currentLineItem.tooling_cost || 0).toFixed(2)}
            </Descriptions.Item>
            <Descriptions.Item label="Operation Outsourced">
              {currencySymbol} {operationOutsourcedCost.toFixed(2)}
            </Descriptions.Item>
            <Descriptions.Item label="Calculated Unit Cost" span={2}>
              <Text strong style={{ fontSize: 16 }}>
                {currencySymbol} {correctCalculatedUnitCost.toFixed(4)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Total Cost (Qty × Unit)" span={2}>
              <Text strong style={{ fontSize: 16 }}>
                {currencySymbol} {totalCost.toFixed(2)}
              </Text>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title="Costing Mode" size="small" style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Text strong>Use Manual Cost Override</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {hasCostBreakdown
                    ? 'Materials and operations drive unit cost. Manual override is ignored while a cost breakdown exists.'
                    : useManualCost
                      ? 'Using manual unit cost. Toggle off to use calculated cost when materials or operations are added.'
                      : 'Using calculated cost from materials & operations.'}
                </Text>
              </div>
              <Switch
                checked={useManualCost}
                onChange={onToggleManualCost}
                checkedChildren="Manual"
                unCheckedChildren="Auto"
                disabled={hasCostBreakdown || manualCostLoading}
                loading={manualCostLoading}
              />
            </div>
          </Space>
        </Card>

        <Card title="Margin Control" size="small" style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Text>BOQ Default Margin: <Tag color="default">{boqMargin}%</Tag></Text>
            </div>
            <div>
              <Text>Item-Specific Margin: </Text>
              <Space>
                <InputNumber
                  min={-100}
                  precision={2}
                  value={marginInput}
                  onChange={handleMarginInputChange}
                  placeholder="Use BOQ default"
                  style={{ width: 150 }}
                  suffix="%"
                  disabled={lineItemDrawerBusy}
                />
                <Button
                  type="primary"
                  size="small"
                  onClick={() => onUpdateMargin(marginInput)}
                  loading={marginActionLoading}
                  disabled={lineItemDrawerBusy && !marginActionLoading}
                >
                  Update
                </Button>
              </Space>
            </div>
            <div>
              <Text>Target Unit Selling Price: </Text>
              <Space>
                <InputNumber
                  min={0}
                  precision={4}
                  value={sellingPriceInput}
                  onChange={handleSellingPriceInputChange}
                  placeholder="Derived from margin"
                  style={{ width: 180 }}
                  prefix={currencySymbol}
                  disabled={lineItemDrawerBusy}
                />
                <Button
                  type="primary"
                  size="small"
                  onClick={handleUpdateFromSellingPrice}
                  loading={marginActionLoading}
                  disabled={lineItemDrawerBusy && !marginActionLoading}
                >
                  Update
                </Button>
                <Button
                  size="small"
                  onClick={handleClearMargin}
                  loading={marginActionLoading}
                  disabled={lineItemDrawerBusy && !marginActionLoading}
                >
                  Clear
                </Button>
              </Space>
            </div>
            <div>
              <Text strong>
                Effective Margin: <Tag color="blue">{roundBoqMarginPct(previewEffectiveMargin)}%</Tag>
              </Text>
            </div>
          </Space>
        </Card>

        <Card title="Selling Price" size="small">
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Unit Cost">
              <Space>
                {currencySymbol} {basisUnit.toFixed(4)}
                {useManualCost && (
                  <Tag color="orange">Using Manual Cost</Tag>
                )}
                {hasCostBreakdown && !useManualCost && (
                  <Tag color="blue">From Materials & Operations</Tag>
                )}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={`Margin (${roundBoqMarginPct(previewEffectiveMargin)}%)`}>
              {currencySymbol} {previewMarginPerUnit.toFixed(4)}
            </Descriptions.Item>
            <Descriptions.Item label="Unit Selling Price">
              <Text strong style={{ fontSize: 16, color: '#52c41a' }}>
                {currencySymbol} {previewUnitSelling.toFixed(4)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Line Selling Total">
              <Text strong style={{ fontSize: 18, color: '#52c41a' }}>
                {currencySymbol} {previewLineSelling.toFixed(2)}
              </Text>
            </Descriptions.Item>
          </Descriptions>
          <div style={{ marginTop: 16 }}>
            <Button
              type="primary"
              icon={<CalculatorOutlined />}
              onClick={onRecalculate}
              block
              loading={recalculateLoading}
              disabled={lineItemDrawerBusy && !recalculateLoading}
            >
              Recalculate Costs
            </Button>
          </div>
        </Card>
      </div>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/growth/estimations/boq')}
                style={{ marginBottom: 16 }}
              >
                Back to List
              </Button>
              <Title level={2}>
                {id
                  ? `Edit BOQ — ${boqData?.boq_number || boqData?.boq_name || id}`
                  : 'Create New BOQ'}
              </Title>
              {id && boqData?.sales_team_name ? (
                <div>
                  <Text type="secondary">Sales team: {boqData.sales_team_name}</Text>
                </div>
              ) : null}
            </div>
            <Space>
              {id && (
                <Button icon={<PrinterOutlined />} onClick={() => openBoqEstimationPrint(id)}>
                  Print
                </Button>
              )}
              {id && (
                <ReportDocumentActions
                  reportKey="boq-estimation"
                  params={{ id }}
                  documentId={id}
                  sendPermissions={['crm.estimation.edit', 'growth.view']}
                />
              )}
              {id && (
                <Button icon={<SwapOutlined />} onClick={() => setTransferOpen(true)}>
                  Transfer team
                </Button>
              )}
              {id && (
                <Button
                  icon={<CalculatorOutlined />}
                  onClick={() => recalculateMutation.mutate(id)}
                  loading={recalculateMutation.isPending}
                >
                  Recalculate
                </Button>
              )}
              {id && canConvertBoqToQuotation && boqStatusAllowsConversion && convertLineCandidates.length > 0 && (
                <Button icon={<FileTextOutlined />} onClick={handleOpenConvertModal}>
                  Convert to Quotation
                </Button>
              )}
              {id && (boqData?.status === 'Draft' || boqData?.status === 'Rejected') && (
                <Button
                  onClick={handleSubmitForReview}
                  loading={submitForReviewMutation.isPending}
                >
                  Submit for Review
                </Button>
              )}
              {id && boqData?.status === 'In Review' && (
                <>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={handleApproveBOQ}
                    loading={approveBOQMutation.isPending}
                  >
                    Approve
                  </Button>
                  <Button
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={handleRejectBOQ}
                    loading={rejectBOQMutation.isPending}
                  >
                    Reject
                  </Button>
                </>
              )}
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={createBOQMutation.isPending || updateBOQMutation.isPending}
              >
                Save BOQ
              </Button>
            </Space>
          </div>

          {id ? (
            <SalesTeamTransferModal
              open={transferOpen}
              onClose={() => setTransferOpen(false)}
              entityType="BoqHeader"
              entityId={id}
              entityLabel={boqData?.boq_number || boqData?.boq_name}
              currentTeamName={boqData?.sales_team_name}
              onSuccess={() => queryClient.invalidateQueries()}
            />
          ) : null}
          {id ? <TransferHistoryPanel entityType="BoqHeader" entityId={id} /> : null}

          <Divider />

          {/* BOQ Header Form */}
          <Form
            form={form}
            layout="vertical"
            onValuesChange={(changed) => {
              if (!('opportunityId' in changed)) return;
              if ('revenueServiceTypeId' in changed) return;
              const val = changed.opportunityId as string | undefined | null;
              if (!val || !String(val).trim()) {
                return;
              }
              const o = opportunitySelectOptions.find((x) => x.id === String(val).trim());
              if (o?.customer_id) {
                form.setFieldValue('customerId', o.customer_id);
              }
              if (o?.revenue_service_type_id) {
                form.setFieldValue('revenueServiceTypeId', o.revenue_service_type_id);
              }
            }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="boqName"
                  label="BOQ Name"
                  rules={[{ required: true, message: 'Please enter BOQ name' }]}
                >
                  <Input placeholder="e.g., Office Furniture Supply" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="boqDate" label="BOQ Date" initialValue={dayjs()}>
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="validUntilDate" label="Valid Until">
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="customerId" label="Customer Name">
                  <Select
                    showSearch
                    placeholder="Select customer"
                    loading={isLoadingCustomers}
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      String(option?.label ?? option?.children ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {customers?.map((customer) => (
                      <Option key={customer.id} value={customer.id}>
                        {customer.customer_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="projectId"
                  label="Project"
                  rules={[{ required: true, message: 'Please select or create a project' }]}
                >
                  <ProjectSelector
                    customerId={form.getFieldValue('customerId')}
                    customerName={customers?.find(c => c.id === form.getFieldValue('customerId'))?.customer_name}
                    allowClear={false}
                    onChange={(projectId, projectData) => {
                      form.setFieldsValue({
                        projectId: projectId,
                        projectName: projectData?.project_name
                      });
                      
                      // Check if project has partner SO (for variation detection)
                      if (projectData?.sales_order_id) {
                        setProjectHasPartnerSO(true);
                        setPartnerSONumber(projectData?.sales_order_id);
                      } else {
                        setProjectHasPartnerSO(false);
                        setPartnerSONumber('');
                      }
                    }}
                  />
                </Form.Item>
                <Form.Item name="projectName" hidden>
                  <Input />
                </Form.Item>
                {projectHasPartnerSO && (
                  <Alert
                    message="Variation Estimation"
                    description={`This project already has a Partner Customer Order (${partnerSONumber}). This estimation will be marked as a Variation.`}
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}
              </Col>
              <Col span={4}>
                <Form.Item name="currency" label="Currency" initialValue="USD">
                  <Select showSearch optionFilterProp="children" placeholder="Select currency">
                    {(allCurrencies || []).filter((c) => c.is_active).length > 0
                      ? (allCurrencies || [])
                          .filter((c) => c.is_active)
                          .map((c) => (
                            <Option key={c.currency_code} value={c.currency_code}>
                              {c.currency_code} — {c.currency_name}
                            </Option>
                          ))
                      : ['USD', 'EUR', 'GBP', 'AED', 'SAR'].map((code) => (
                          <Option key={code} value={code}>
                            {code}
                          </Option>
                        ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item name="marginPercentage" label="Margin %" initialValue={0}>
                  <InputNumber min={0} max={100} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="notes" label="Notes">
                  <TextArea rows={2} placeholder="Additional notes or requirements" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Card size="small" title="CRM link" style={{ marginBottom: 8 }}>
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    {linkedOppIdForFetch ? (
                      <Space wrap>
                        <span>
                          <Text strong>
                            {boqData?.opportunity?.opportunity_number ??
                              linkedOppDetail?.opportunity_number ??
                              '—'}
                          </Text>
                          {(boqData?.opportunity?.opportunity_name ?? linkedOppDetail?.opportunity_name) ? (
                            <Text>
                              {' '}
                              — {boqData?.opportunity?.opportunity_name ?? linkedOppDetail?.opportunity_name}
                            </Text>
                          ) : null}
                        </span>
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0 }}
                          onClick={() => navigate(`/growth/opportunities/${linkedOppIdForFetch}`)}
                        >
                          Open opportunity
                        </Button>
                      </Space>
                    ) : (
                      <Text type="secondary">No CRM opportunity linked. Select one below or leave empty.</Text>
                    )}
                    <Form.Item name="opportunityId" label="Linked opportunity" style={{ marginBottom: 0 }}>
                      <Select
                        placeholder="Select opportunity (optional)"
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        options={opportunitySelectOptions.map((o) => ({
                          value: o.id,
                          label: `${o.opportunity_number} — ${o.opportunity_name}`,
                        }))}
                      />
                    </Form.Item>
                    <Form.Item name="revenueServiceTypeId" label="Revenue service type">
                      <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="Select type (optional)"
                        options={rstSelectOptions}
                      />
                    </Form.Item>
                  </Space>
                </Card>
              </Col>
            </Row>
          </Form>

          {id && (
            <Card size="small" title={`Related Quotations (${relatedQuotations.length})`}>
              <Table
                size="small"
                rowKey="id"
                pagination={false}
                locale={{ emptyText: 'No quotations created from this BOQ yet.' }}
                dataSource={relatedQuotations}
                columns={[
                  {
                    title: 'Quotation',
                    key: 'quotation_number',
                    render: (_, record) => (
                      <Button
                        type="link"
                        style={{ padding: 0 }}
                        onClick={() => navigate(`/growth/quotations/${record.id}`)}
                      >
                        {record.quotation_number}
                      </Button>
                    ),
                  },
                  {
                    title: 'Date',
                    key: 'quotation_date',
                    render: (_, record) =>
                      record.quotation_date ? dayjs(record.quotation_date).format('DD MMM YYYY') : '—',
                  },
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    key: 'status',
                  },
                  {
                    title: 'Selected lines',
                    key: 'selected_lines',
                    render: (_, record) =>
                      (record.items || []).filter((item) => Boolean(item.boq_line_item_id)).length,
                  },
                  {
                    title: 'Value',
                    key: 'value',
                    render: (_, record) => `${currencySymbol} ${Number(record.grand_total || 0).toFixed(2)}`,
                  },
                  {
                    title: '',
                    key: 'resync',
                    render: (_, record) =>
                      record.is_latest_revision !== false && !record.is_converted_to_sales_order ? (
                        <Button
                          size="small"
                          onClick={() => navigate(`/growth/quotations/${record.id}/source-sync`)}
                        >
                          Resync quotation
                        </Button>
                      ) : null,
                  },
                ]}
              />
            </Card>
          )}

          <Divider />

          {/* Categories and Line Items */}
          <Tabs
            activeKey={activeCategory}
            onChange={setActiveCategory}
            type="editable-card"
            onEdit={(targetKey, action) => {
              if (action === 'add') {
                handleAddCategory();
              }
            }}
            items={categories.map((category, index) => ({
              key: index.toString(),
              label: (
                <span>
                  {category.name}
                  <Tag color="blue" style={{ marginLeft: 8 }}>
                    {category.items.length}
                  </Tag>
                </span>
              ),
              closable: categories.length > 1,
              children: (
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddItem} block>
                      Add Line Item to {category.name}
                    </Button>
                  </div>

                  <Table
                    columns={itemColumns}
                    dataSource={category.items}
                    rowKey={(record, idx) => `${category.name}-${idx}`}
                    pagination={false}
                    scroll={{ x: 1500 }}
                    summary={() => (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={7} align="right">
                            <Text strong>Category Total:</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align="right">
                            <Text strong style={{ fontSize: 16, color: '#145A54' }}>
                              {currencySymbol} {Number(categoryCostTotals[index] || 0).toFixed(2)}
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} />
                          <Table.Summary.Cell index={3} align="right">
                            <Text strong style={{ fontSize: 16, color: '#52c41a' }}>
                              {currencySymbol} {Number(categorySellingTotals[index] || 0).toFixed(2)}
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={4} colSpan={4} />
                        </Table.Summary.Row>
                      </Table.Summary>
                    )}
                  />
                </div>
              ),
              onClose: () => handleDeleteCategory(index)
            }))}
          />

          <Divider />

          {/* Cost Summary */}
          <Card title="Cost Summary" size="small">
            <Row gutter={16}>
              <Col span={8}>
                <Divider orientation="left">Totals</Divider>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>Grand Cost Total:</Text>
                    <Text strong style={{ fontSize: 20, color: '#000' }}>
                      {currencySymbol} {grandCostTotal.toFixed(2)}
                    </Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>Total markup (selling − cost):</Text>
                    <Text style={{ fontSize: 16, color: '#52c41a' }}>
                      {currencySymbol} {marginAmount.toFixed(2)}
                    </Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text strong>Grand Selling Total:</Text>
                    <Text strong style={{ fontSize: 24, color: '#52c41a' }}>
                      {currencySymbol} {grandSellingTotal.toFixed(2)}
                    </Text>
                  </div>
                </Space>
              </Col>
              <Col span={16}>
                <Divider orientation="left">Category Breakdown</Divider>
                <Row gutter={[16, 16]}>
                  {categories.map((category, index) => (
                    <Col span={12} key={index}>
                      <Card size="small">
                        <Text strong>{category.name}</Text>
                        <Tag color="blue" style={{ marginLeft: 8 }}>
                          {category.items.length} items
                        </Tag>
                        <Row gutter={16} style={{ marginTop: 8 }}>
                          <Col span={12}>
                            <Text type="secondary">Cost</Text>
                            <Statistic
                              value={categoryCostTotals[index] || 0}
                              precision={2}
                              prefix={currencySymbol}
                              valueStyle={{ fontSize: 16 }}
                            />
                          </Col>
                          <Col span={12}>
                            <Text type="secondary">Selling</Text>
                            <Statistic
                              value={categorySellingTotals[index] || 0}
                              precision={2}
                              prefix={currencySymbol}
                              valueStyle={{ fontSize: 16, color: '#52c41a' }}
                            />
                          </Col>
                        </Row>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Col>
            </Row>
          </Card>

          <Divider />

          {/* Documents Section */}
          {id && (
            <Card
              title="Documents"
              size="small"
              extra={
                <Button
                  type="primary"
                  icon={<SyncOutlined />}
                  disabled={!boqData?.opportunity_id}
                  loading={syncingOpportunityDocs}
                  title={boqData?.opportunity_id ? undefined : 'Link an opportunity to sync documents'}
                  onClick={async () => {
                    if (!id) return;
                    setSyncingOpportunityDocs(true);
                    try {
                      const r = await boqService.syncOpportunityDocuments(id);
                      message.success(
                        r.copied > 0
                          ? `Copied ${r.copied} document(s) from the opportunity`
                          : 'No new documents to copy from the opportunity'
                      );
                      await queryClient.invalidateQueries({ queryKey: documentKeys.byEntity('BOQ', id) });
                    } catch (e: any) {
                      message.error(e?.response?.data?.message || e?.message || 'Sync failed');
                    } finally {
                      setSyncingOpportunityDocs(false);
                    }
                  }}
                >
                  Sync documents from opportunity
                </Button>
              }
            >
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                {!boqData?.opportunity_id ? (
                  <Text type="secondary">Link an opportunity on this BOQ to sync its documents.</Text>
                ) : null}
                <DocumentUpload
                  entityType="BOQ"
                  entityId={id}
                  onSuccess={() => {
                    // Refetch will be handled by DocumentList component
                  }}
                />
                <DocumentList entityType="BOQ" entityId={id} />
              </Space>
              <InheritedAttachmentsCard trailDomain="sales" entityType="BOQ" entityId={id} />
            </Card>
          )}
        </Space>
      </Card>

      {/* Line Item Drawer with Tabs */}
      <Drawer
        title={editingItem ? 'Edit Line Item' : 'Add Line Item'}
        open={itemModalVisible}
        onClose={() => {
          if (lineItemDrawerBusy) return;
          setItemModalVisible(false);
          setCurrentLineItemId(null);
          setActiveTab('basic');
        }}
        maskClosable={!lineItemDrawerBusy}
        keyboard={!lineItemDrawerBusy}
        closable={!lineItemDrawerBusy}
        width={1000}
        extra={
          <Space>
            <Button
              onClick={() => {
                if (lineItemDrawerBusy) return;
                setItemModalVisible(false);
                setCurrentLineItemId(null);
                setActiveTab('basic');
              }}
              disabled={lineItemDrawerBusy}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleItemModalOk}
              icon={<SaveOutlined />}
              loading={updateLineItemMutation.isPending || addLineItemsMutation.isPending}
              disabled={lineItemDrawerBusy && !updateLineItemMutation.isPending && !addLineItemsMutation.isPending}
            >
              {editingItem ? 'Update' : 'Add'}
            </Button>
          </Space>
        }
      >
        <Spin spinning={lineItemDrawerBusy} tip="Processing…">
        {!currentLineItemId && (
          <Alert
            message="Save Required"
            description="Save the BOQ and this line item first to manage materials and operations."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            if (lineItemDrawerBusy) return;
            setActiveTab(key);
          }}
        >
          {/* Basic Info Tab */}
          <Tabs.TabPane tab="Basic Info" key="basic">
            <Form form={itemForm} layout="vertical" disabled={lineItemDrawerBusy}>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="itemCode" label="Item Code">
                    <Input placeholder="SKU/Code" />
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item
                    name="itemName"
                    label={
                      <Space size="small" wrap>
                        <span>Item Name</span>
                        {currentLineItemId ? (
                          <Button
                            type="link"
                            size="small"
                            onClick={openPricingHistoryModal}
                            disabled={lineItemDrawerBusy}
                            style={{ padding: 0, height: 'auto' }}
                          >
                            Pricing from history
                          </Button>
                        ) : (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            (Save line to copy pricing from history)
                          </Text>
                        )}
                      </Space>
                    }
                    rules={[{ required: true, message: 'Required' }]}
                  >
                    <Input placeholder="Item name" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="description" label="Description">
                <TextArea rows={2} placeholder="Item description" />
              </Form.Item>

              <Form.Item name="revision" label="Revision">
                <Input placeholder="Drawing / spec revision (e.g. D, 0)" maxLength={50} />
              </Form.Item>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="quantity"
                    label="Quantity"
                    rules={[{ required: true, message: 'Required' }]}
                  >
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="uom" label="UOM" rules={[{ required: true, message: 'Required' }]}>
                    <Select placeholder="Select UOM">
                      <Option value="pcs">Pieces</Option>
                      <Option value="kg">Kilograms</Option>
                      <Option value="m">Meters</Option>
                      <Option value="sqm">Square Meters</Option>
                      <Option value="box">Box</Option>
                      <Option value="set">Set</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="unitCost"
                    label={`Unit Cost (${currencySymbol})`}
                    rules={[{ required: true, message: 'Required' }]}
                    help={editingItem?.boqLineItem ? 
                      (editingItem.boqLineItem.use_manual_cost ? 
                        "Manual cost mode - edit as needed" : 
                        `Auto-calculated: ${currencySymbol} ${Number(editingItem.boqLineItem.calculated_unit_cost || 0).toFixed(2)}`) 
                      : "Initial estimate - will auto-calculate from materials/operations"}
                  >
                    <InputNumber min={0} precision={4} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="costBasis" label="Cost Basis">
                    <Select>
                      <Option value="Estimated">Estimated</Option>
                      <Option value="Supplier Quote">Supplier Quote</Option>
                      <Option value="Last Purchase">Last Purchase</Option>
                      <Option value="Standard Cost">Standard Cost</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="leadTimeDays" label="Lead Time (Days)">
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="supplierName" label="Supplier">
                    <Input placeholder="Supplier name" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="marginPercentage" label="Item Margin %" help="Leave empty to use BOQ margin">
                    <InputNumber min={0} max={100} precision={2} style={{ width: '100%' }} placeholder="Optional" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="notes" label="Notes">
                <TextArea rows={2} placeholder="Additional notes" />
              </Form.Item>
            </Form>
          </Tabs.TabPane>

          {/* Materials Tab */}
          <Tabs.TabPane 
            tab={<span><ToolOutlined /> Materials</span>} 
            key="materials" 
            disabled={!currentLineItemId}
          >
            {currentLineItemId && id && (
              <MaterialsTabContent
                boqId={id}
                lineItemId={currentLineItemId}
                currencySymbol={currencySymbol}
                onAddMaterial={handleAddMaterial}
                onEditMaterial={handleEditMaterial}
                onDeleteMaterial={handleDeleteMaterial}
              />
            )}
          </Tabs.TabPane>

          {/* Operations Tab */}
          <Tabs.TabPane 
            tab={<span><ToolOutlined /> Operations</span>} 
            key="operations" 
            disabled={!currentLineItemId}
          >
            {currentLineItemId && id && (
              <OperationsTabContent
                boqId={id}
                lineItemId={currentLineItemId}
                currencySymbol={currencySymbol}
                onAddOperation={handleAddOperation}
                onEditOperation={handleEditOperation}
                onDeleteOperation={handleDeleteOperation}
              />
            )}
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={<span><PaperClipOutlined /> Attachments</span>}
            key="attachments"
            disabled={!currentLineItemId}
          >
            {currentLineItemId && id ? (
              <EntityAttachmentsCard
                entityType={DOCUMENT_ENTITY_TYPE_BOQ_LINE_ITEM}
                entityId={currentLineItemId}
                title="Attachments"
                documentCategory={BOQ_LINE_DOCUMENT_CATEGORY_ATTACHMENT}
                categoryExcludes={BOQ_LINE_DOCUMENT_CATEGORY_TECHNICAL}
                queryKeyPrefix={['boq-line-attachments', id, 'general']}
                sharedInvalidateKey={['boq-line-attachments', id]}
                uploadHint="Supporting files for this line. Drawings and specifications belong on Technical Attachments."
                emptyText="No attachments"
              />
            ) : null}
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={<span><FileTextOutlined /> Technical Attachments</span>}
            key="technical-attachments"
            disabled={!currentLineItemId}
          >
            {currentLineItemId && id ? (
              <EntityAttachmentsCard
                entityType={DOCUMENT_ENTITY_TYPE_BOQ_LINE_ITEM}
                entityId={currentLineItemId}
                title="Technical attachments"
                documentCategory={BOQ_LINE_DOCUMENT_CATEGORY_TECHNICAL}
                categoryEquals={BOQ_LINE_DOCUMENT_CATEGORY_TECHNICAL}
                queryKeyPrefix={['boq-line-attachments', id, 'technical']}
                sharedInvalidateKey={['boq-line-attachments', id]}
                uploadHint="Drawings, specifications, and other technical documents. These open from work orders, shop floor, and other manufacturing screens."
                emptyText="No technical attachments"
              />
            ) : null}
          </Tabs.TabPane>

          {/* Cost Summary Tab */}
          <Tabs.TabPane 
            tab={<span><CalculatorOutlined /> Cost Summary</span>} 
            key="costSummary" 
            disabled={!currentLineItemId}
          >
            {currentLineItemId && id && (
              <CostSummaryTabContent
                boqId={id}
                lineItemId={currentLineItemId}
                lineItem={editingItem?.boqLineItem}
                currencySymbol={currencySymbol}
                boqMargin={form.getFieldValue('marginPercentage') || 0}
                onToggleManualCost={async (useManual: boolean) => {
                  if (!id || !currentLineItemId) return;
                  await toggleManualCostMutation.mutateAsync({
                    boqId: id,
                    lineItemId: currentLineItemId,
                    useManual
                  });
                  await patchLineItemDrawerAfterBoqRefetch(currentLineItemId);
                }}
                onUpdateMargin={async (margin: number | null) => {
                  if (!id || !currentLineItemId) return;
                  await updateMarginMutation.mutateAsync({
                    boqId: id,
                    lineItemId: currentLineItemId,
                    marginPercentage: margin
                  });
                  await patchLineItemDrawerAfterBoqRefetch(currentLineItemId);
                }}
                onRecalculate={async () => {
                  if (!id || !currentLineItemId) return;
                  await recalculateLineItemMutation.mutateAsync({
                    boqId: id,
                    lineItemId: currentLineItemId
                  });
                  await patchLineItemDrawerAfterBoqRefetch(currentLineItemId);
                }}
                lineItemDrawerBusy={lineItemDrawerBusy}
                manualCostLoading={toggleManualCostMutation.isPending}
                marginActionLoading={updateMarginMutation.isPending}
                recalculateLoading={recalculateLineItemMutation.isPending}
              />
            )}
          </Tabs.TabPane>
        </Tabs>
        </Spin>
      </Drawer>

      <Modal
        title="Create Quotation from BOQ"
        open={convertModalOpen}
        width={980}
        onCancel={() => setConvertModalOpen(false)}
        onOk={handleConvertSelectedLines}
        okText={convertMode === 'revise' ? 'Create Revision' : 'Create Quotation'}
        okButtonProps={{
          loading:
            createQuotationFromBoqMutation.isPending || createQuotationRevisionMutation.isPending,
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="BOQ">{boqData?.boq_number || '—'}</Descriptions.Item>
            <Descriptions.Item label="Status">{boqData?.status || '—'}</Descriptions.Item>
            <Descriptions.Item label="Customer">{boqData?.customer_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Opportunity">
              {boqData?.opportunity?.opportunity_number || linkedOppDetail?.opportunity_number || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Project">{boqData?.project_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="BOQ Value">
              {currencySymbol} {Number(boqData?.selling_price || 0).toFixed(2)}
            </Descriptions.Item>
            <Descriptions.Item label="Existing quotations">
              {relatedQuotations.length}
            </Descriptions.Item>
            <Descriptions.Item label="Selected lines">{selectedConvertLineIds.length}</Descriptions.Item>
          </Descriptions>

          {relatedQuotations.length > 0 ? (
            <Alert
              type="info"
              showIcon
              message="Existing quotations for this BOQ"
              description={
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Radio.Group value={convertMode} onChange={(e) => setConvertMode(e.target.value)}>
                    <Space direction="vertical">
                      <Radio value="revise">
                        Create revision of an existing quotation (same commercial number)
                      </Radio>
                      <Radio value="new">Create a separate quotation (new number)</Radio>
                    </Space>
                  </Radio.Group>
                  {convertMode === 'revise' ? (
                    <Select
                      style={{ width: '100%' }}
                      placeholder="Select quotation to revise"
                      value={reviseTargetQuotationId}
                      onChange={setReviseTargetQuotationId}
                      options={relatedQuotations.map((q) => ({
                        value: q.id,
                        label: `${q.quotation_number} · ${displayQuotationRevisionLabel(q.revision_number)} — ${q.status}`,
                      }))}
                    />
                  ) : null}
                </Space>
              }
            />
          ) : null}

          {convertMode === 'revise' ? (
            <Alert
              type="warning"
              showIcon
              message="Revision keeps the same quotation number and copies commercial content into a new Draft. Re-cost line import from BOQ remains a follow-up edit on the revision."
            />
          ) : (
            <>
          <div>
            <Text strong>
              Department <Text type="danger">*</Text>
            </Text>
            <div style={{ marginTop: 8 }}>
              <DepartmentSelect
                value={convertDepartmentId}
                onChange={(value) => setConvertDepartmentId(value || undefined)}
                style={{ width: '100%' }}
                placeholder="Select department"
              />
            </div>
          </div>

          <Space>
            <Button onClick={() => setSelectedConvertLineIds(convertLineCandidates.map((line) => line.id))}>
              Select all
            </Button>
            <Button onClick={() => setSelectedConvertLineIds([])}>Deselect all</Button>
          </Space>

          <Table
            size="small"
            rowKey="id"
            pagination={{ pageSize: 8 }}
            dataSource={convertLineCandidates}
            rowSelection={{
              selectedRowKeys: selectedConvertLineIds,
              onChange: (keys) => setSelectedConvertLineIds(keys.map((k) => String(k))),
            }}
            columns={[
              { title: '#', dataIndex: 'line_number', key: 'line_number', width: 60 },
              { title: 'Item', dataIndex: 'item_name', key: 'item_name', ellipsis: true },
              { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
              { title: 'Qty', dataIndex: 'quantity', key: 'quantity', width: 100 },
              { title: 'UOM', dataIndex: 'uom', key: 'uom', width: 80 },
              {
                title: 'Unit Rate',
                key: 'unit_rate',
                render: (_, row) => `${currencySymbol} ${Number(row.unit_selling_price || row.unit_cost || 0).toFixed(2)}`,
              },
              {
                title: 'Amount',
                key: 'amount',
                render: (_, row) => `${currencySymbol} ${Number(row.line_selling_total || row.line_total || 0).toFixed(2)}`,
              },
              {
                title: 'Already quoted',
                key: 'already_quoted',
                render: (_, row) => (lineIdsAlreadyQuoted.has(row.id) ? <Tag color="gold">Yes</Tag> : '—'),
              },
            ]}
          />
            </>
          )}
        </Space>
      </Modal>

      <Modal
        title="Pricing from history"
        open={pricingHistoryModalOpen}
        onCancel={() => setPricingHistoryModalOpen(false)}
        footer={null}
        width={900}
        destroyOnClose
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Search past BOQ lines in your company. Use copies materials, operations, and line cost fields to this line.
            This line&apos;s name, code, quantity, and UOM are not changed.
          </Text>
          <InputSearch
            placeholder="At least 2 characters — item code, name, or description"
            value={historySearchText}
            onChange={(e) => setHistorySearchText(e.target.value)}
            onSearch={(v) => setPricingHistoryQuery(String(v).trim())}
            enterButton={
              <span>
                <SearchOutlined /> Search
              </span>
            }
            loading={pricingHistoryLoading}
          />
          <Table<BoqLineItemPricingHistoryRow>
            size="small"
            rowKey="line_item_id"
            loading={pricingHistoryLoading}
            dataSource={pricingHistoryRows}
            pagination={{ pageSize: 8 }}
            columns={pricingHistoryColumns}
            locale={{ emptyText: pricingHistoryQuery.trim().length < 2 ? 'Enter at least 2 characters and click Search' : 'No matches' }}
          />
        </Space>
      </Modal>

      {/* Material Add/Edit Modal */}
      <Modal
        title={editingMaterial ? 'Edit Material' : 'Add Material'}
        open={materialModalVisible}
        onOk={handleMaterialModalOk}
        onCancel={() => {
          if (materialModalSaving) return;
          setMaterialModalVisible(false);
          setEditingMaterial(null);
        }}
        confirmLoading={materialModalSaving}
        okText={materialModalSaving ? 'Saving…' : 'OK'}
        okButtonProps={{ loading: materialModalSaving }}
        cancelButtonProps={{ disabled: materialModalSaving }}
        maskClosable={!materialModalSaving}
        keyboard={!materialModalSaving}
        closable={!materialModalSaving}
        width={700}
      >
        <Form form={materialForm} layout="vertical" disabled={materialModalSaving}>
          <Form.Item
            name="linkMode"
            label="Inventory link"
            initialValue="inventory"
            rules={[{ required: true }]}
            help="Description-only materials can be linked to inventory later on the Work Order Material Request."
          >
            <Select
              options={[
                { value: 'inventory', label: 'Link inventory item now' },
                { value: 'description', label: 'Description only (link later on Material Request)' },
              ]}
              onChange={(mode) => {
                if (mode === 'description') {
                  setSelectedItem(null);
                  materialForm.setFieldsValue({ item_id: undefined });
                }
              }}
            />
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, next) => prev.linkMode !== next.linkMode}>
            {() => {
              const linkMode = materialForm.getFieldValue('linkMode') === 'description' ? 'description' : 'inventory';
              if (linkMode !== 'inventory') return null;
              return (
                <Form.Item
                  name="item_id"
                  label="Select Item from Inventory"
                  rules={[{ required: true, message: 'Please select an item' }]}
                >
                  <ItemSelect
                    seedItems={items}
                    allowQuickCreate
                    onItemCreated={(created) => {
                      setItems((prev) => {
                        if (prev.some((i) => i.id === created.id)) return prev;
                        return [...prev, created];
                      });
                      handleItemSelect(created.id, created);
                    }}
                    onChange={(v) => {
                      if (v) handleItemSelect(String(v));
                    }}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item
            name="description"
            label="Material / Description"
            rules={[{ required: true, message: 'Required' }]}
            help="Required. Auto-filled when an inventory item is selected; editable either way."
          >
            <TextArea
              rows={2}
              placeholder="Enter material description"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="quantity"
                label="Quantity"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber min={0} precision={4} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="uom"
                label="UOM"
                rules={[{ required: true, message: 'Required' }]}
                help="Auto-filled, editable"
              >
                <Select>
                  <Option value="pcs">Pieces</Option>
                  <Option value="kg">Kilograms</Option>
                  <Option value="m">Meters</Option>
                  <Option value="L">Liters</Option>
                  <Option value="sqm">Square Meters</Option>
                  <Option value="ft">Feet</Option>
                  <Option value="ton">Tons</Option>
                  <Option value="box">Box</Option>
                  <Option value="set">Set</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="unitCost"
                label={`Unit Cost (${currencySymbol})`}
                rules={[{ required: true, message: 'Required' }]}
                help="Auto-filled, editable"
              >
                <InputNumber min={0} precision={4} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="costBasis" label="Cost Basis" initialValue="Estimated">
                <Select>
                  <Option value="Estimated">Estimated</Option>
                  <Option value="Supplier Quote">Supplier Quote</Option>
                  <Option value="Last Purchase">Last Purchase</Option>
                  <Option value="Standard Cost">Standard Cost</Option>
                  <Option value="Average Cost">Average Cost</Option>
                  <Option value="Historical">Historical</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="supplierName" label="Supplier">
                <Input placeholder="Supplier name" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={2} placeholder="Additional notes" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Operation Add/Edit Modal */}
      <Modal
        title={editingOperation ? 'Edit Operation' : 'Add Operation'}
        open={operationModalVisible}
        onOk={handleOperationModalOk}
        onCancel={handleOperationModalCancel}
        confirmLoading={operationModalSaving}
        okText={operationModalSaving ? 'Saving…' : 'OK'}
        okButtonProps={{ loading: operationModalSaving }}
        cancelButtonProps={{ disabled: operationModalSaving }}
        maskClosable={!operationModalSaving}
        keyboard={!operationModalSaving}
        closable={!operationModalSaving}
        width={800}
      >
        <Form form={operationForm} layout="vertical" disabled={operationModalSaving}>
          <Row gutter={16}>
            <Col span={18}>
              <Form.Item
                name="operationId"
                label="Operation"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (getFieldValue('isOutsourced')) return Promise.resolve();
                      if (!value) return Promise.reject(new Error('Required'));
                      return Promise.resolve();
                    }
                  })
                ]}
              >
                <Select
                  placeholder="Select operation (optional if outsourced)"
                  allowClear
                  loading={isLoadingOperations}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option: any) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  onSearch={setOperationSelectSearch}
                  onChange={(value) => {
                    const selectedOp = operations.find(op => op.id === value);
                    if (selectedOp) {
                      operationForm.setFieldsValue({
                        description: `${selectedOp.operation_name} - ${selectedOp.description || ''}`,
                        setupTimeHours: selectedOp.standard_setup_time_hours,
                        runTimePerUnitHours: selectedOp.standard_run_time_per_unit_hours,
                        machineRatePerHour: Number(selectedOp.per_hour_cost ?? 0),
                      });
                    }
                  }}
                  notFoundContent={
                    isLoadingOperations
                      ? 'Loading operations...'
                      : 'No matching operation — create one below'
                  }
                  popupRender={(menu) => (
                    <>
                      {menu}
                      <Divider style={{ margin: '8px 0' }} />
                      <Button
                        type="link"
                        icon={<PlusOutlined />}
                        block
                        style={{ textAlign: 'left' }}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => setQuickCreateOperationOpen(true)}
                      >
                        Create new operation
                      </Button>
                    </>
                  )}
                  options={operations.map(op => ({
                    value: op.id,
                    label: `${op.operation_code} - ${op.operation_name}`,
                    operation: op
                  }))}
                />
              </Form.Item>
              <Form.Item
                name="description"
                label="Operation Description"
                hidden
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="sequenceNumber" label="Sequence" initialValue={1}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="isOutsourced" label="Is this operation outsourced?" valuePropName="checked" initialValue={false}>
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.isOutsourced !== currentValues.isOutsourced}
          >
            {({ getFieldValue }) => {
              const isOutsourced = getFieldValue('isOutsourced');
              
              if (isOutsourced) {
                return (
                  <>
                    <Form.Item
                      name="outsourceCost"
                      label={`Outsource Cost (${currencySymbol})`}
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="Total outsource cost" />
                    </Form.Item>
                    <Form.Item
                      name="outsourcedSupplierCategoryIds"
                      label="Supplier categories (for procurement)"
                      help="Helps purchasing shortlist suppliers for this outsourced step."
                    >
                      <Select
                        mode="multiple"
                        allowClear
                        placeholder="Select one or more supplier categories"
                        options={supplierCategorySelectOptions}
                        loading={loadingSupplierCategories}
                        showSearch
                        optionFilterProp="label"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                    <Form.Item
                      name="outsourcedScopeDetail"
                      label="Operation outsourced scope detail"
                      rules={[{ required: true, message: 'Describe the outsourced scope of work' }]}
                      help="Vendor-facing scope. Used when starting the outsourced Purchase Request."
                    >
                      <TextArea
                        rows={3}
                        placeholder="Describe what the vendor must perform for this operation"
                      />
                    </Form.Item>
                  </>
                );
              }

              return (
                <>
                  <Divider>Time & Rates</Divider>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="setupTimeHours" label="Setup Time (hours)" initialValue={0}>
                        <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="runTimePerUnitHours" label="Run time (hours)" initialValue={0}>
                        <InputNumber min={0} precision={4} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item name="laborRatePerHour" label={`Labor Rate (${currencySymbol}/hr)`} initialValue={0}>
                        <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="overheadRatePerHour" label={`Overhead Rate (${currencySymbol}/hr)`} initialValue={0}>
                        <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="machineRatePerHour" label={`Machine Rate (${currencySymbol}/hr)`} initialValue={0}>
                        <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="toolingCost" label={`Material Cost (${currencySymbol})`} initialValue={0}>
                        <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              );
            }}
          </Form.Item>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={2} placeholder="Additional notes" />
          </Form.Item>
        </Form>
      </Modal>

      <QuickCreateOperationModal
        open={quickCreateOperationOpen}
        presetName={operationSelectSearch}
        onCancel={() => setQuickCreateOperationOpen(false)}
        onCreated={(op) => {
          setOperations((prev) => (prev.some((x) => x.id === op.id) ? prev : [...prev, op]));
          operationForm.setFieldsValue({
            operationId: op.id,
            description: op.description
              ? `${op.operation_name} - ${op.description}`
              : op.operation_name,
            setupTimeHours: op.standard_setup_time_hours,
            runTimePerUnitHours: op.standard_run_time_per_unit_hours,
            machineRatePerHour: Number(op.per_hour_cost ?? 0),
          });
          setOperationSelectSearch('');
        }}
      />

      {/* Add Category Modal */}
      <Modal
        title="Add New Category"
        open={categoryModalVisible}
        onOk={handleCategoryModalOk}
        onCancel={() => setCategoryModalVisible(false)}
      >
        <Input
          placeholder="Category name"
          value={newCategoryName}
          onChange={e => setNewCategoryName(e.target.value)}
          onPressEnter={handleCategoryModalOk}
        />
      </Modal>
    </div>
  );
};

export default BOQBuilderPage;

